// pages/ProjectInterfacePage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { 
  Calendar,
  Clock,
  Users,
  User,
  Edit,
  Trash2,
  X,
  Plus,
  AlertTriangle,
  Shield,
  Globe,
  Lock,
  Tag,
  FileText,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Mail,
  XCircle,
  PencilIcon,
  ChevronDown,
  ChevronUp,
  UserPlus,
  UserMinus,
  Key,
  Copy,
  Check,
  Kanban,
  LayoutDashboard,
  List,
  GanttChart,
  MessageSquare,
  Paperclip,
  Star,
  ChevronDown as ChevronDownIcon,
  MoreVertical,
  Lightbulb,
  Search,
  Filter,
  ArrowUpDown,
  Grid3x3,
  ChevronRight,
  Circle,
  Flag,
  UserCircle,
  ChevronLeft,
  ZoomIn,
  ZoomOut,
  SlidersHorizontal,
  Sparkles,
  Vote as VoteIcon,
  Users2,
  Gauge,
  AlertCircle,
  TrendingUp,
  BarChart3,
  Calendar as CalendarIcon,
  Play,
  Upload,
  Download,
  File as FileIcon,
  CheckCircle,
  MessageSquare as MessageSquareIcon,
  Eye,
  Send,
  Video,
  Share2,
  Type,
  Image as ImageIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';
import { API_BASE_URL } from '../config/api';
import ProjectNavbar from '../components/ProjectNavbar';
import TaskListView from './TaskListView';
import TeamTasksPage from './TeamTasksPage';
import TimelinePage from './TimelinePage';
import NotesPage from './NotesPage';
import MeetingCalendar from '../components/MeetingCalendar';
import ProjectMemberRoleManager from '../components/ProjectMemberRoleManager';
import MessagesPage from './MessagesPage';

interface ProjectData {
  _id: string;
  title: string;
  description: string;
  start: string;
  end: string;
  type: 'meeting' | 'workshop' | 'deadline' | 'social';
  isPublic: boolean;
  color: string;
  creator: { _id: string; name: string; email: string };
  teams?: TeamData[];
  accessCode?: string;
  myRole?: { role: string; assignedAt?: string } | null;
  isCreator?: boolean;
  upcomingMeetings?: any[];
  importantNotes?: any[];
}

interface TeamData {
  _id: string;
  name: string;
  description: string;
  members: { _id: string; name: string; email: string; globalRole?: string | null; projectRole?: string | null }[];
  invites?: { email: string; status: 'pending' | 'accepted' | 'rejected' }[];
  joinRequests?: {
    _id: string;
    userId: string;
    userName: string;
    userEmail: string;
    status: 'pending' | 'accepted' | 'rejected';
  }[];
  creator: { _id: string; name: string; email: string };
  allowJoinRequests?: boolean;
}

interface FormData {
  title: string;
  description: string;
  start: string;
  end: string;
  type: 'meeting' | 'workshop' | 'deadline' | 'social';
  isPublic: boolean;
}

interface TeamFormData {
  name: string;
  description: string;
  memberEmails: string[];
}

// Composant pour afficher les réunions à venir dans l'Overview
const UpcomingMeetingsSection: React.FC<{ projectId: string; user: any; meetings?: any[] }> = ({ projectId, user, meetings: preloaded }) => {
  const [upcomingMeetings, setUpcomingMeetings] = useState<any[]>(preloaded ?? []);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const API_BASE = API_BASE_URL;

  useEffect(() => {
    if (preloaded !== undefined) {
      setUpcomingMeetings(preloaded);
      setLoading(false);
      return;
    }
    const fetchUpcomingMeetings = async () => {
      if (!projectId || !user?._id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE}/meeting-events/project/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          const now = new Date();
          const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          
          // Filtrer les réunions à venir (pas encore passées, pas annulées)
          const upcoming = response.data.data
            .filter((meeting: any) => {
              const meetingDate = new Date(meeting.scheduledDate);
              return meetingDate >= now && 
                     meetingDate <= nextWeek &&
                     meeting.status !== 'cancelled' &&
                     meeting.status !== 'completed';
            })
            .sort((a: any, b: any) => {
              return new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime();
            })
            .slice(0, 5); // Limiter à 5 réunions les plus proches

          setUpcomingMeetings(upcoming);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des réunions à venir:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUpcomingMeetings();
  }, [projectId, user, preloaded]);

  const formatMeetingDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const meetingDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (meetingDate.getTime() === today.getTime()) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (meetingDate.getTime() === tomorrow.getTime()) {
      return `Tomorrow at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  const getResponseStatus = (meeting: any) => {
    if (!meeting.responses || !user?._id) return null;
    const userResponse = meeting.responses.find((r: any) => r.userId === user._id);
    return userResponse?.status || 'pending';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'declined':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'maybe':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'Accepted';
      case 'declined':
        return 'Declined';
      case 'maybe':
        return 'Maybe';
      default:
        return 'En attente';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (upcomingMeetings.length === 0) {
    return null; // Ne rien afficher s'il n'y a pas de réunions à venir
  }

  return (
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
              <Video className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-purple-400"></div>
                Upcoming meetings
              </h2>
            </div>
          </div>
        <button
          onClick={() => navigate(`/projects/${projectId}?tab=meetings`)}
          className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
        >
          Voir tout
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-3">
        {upcomingMeetings.map((meeting) => {
          const responseStatus = getResponseStatus(meeting);
          const isOrganizer = meeting.organizer._id === user?._id;

          return (
            <div
              key={meeting._id}
              className="border border-gray-200/60 rounded-xl p-4 hover:border-purple-300 hover:shadow-md transition-all duration-200 cursor-pointer bg-white"
              onClick={() => window.open(meeting.meetingLink, '_blank')}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-base font-semibold text-gray-900 truncate">
                      {meeting.title}
                    </h3>
                    {isOrganizer && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                        Organisateur
                      </span>
                    )}
                  </div>

                  {meeting.description && (
                    <p className="text-sm text-gray-600 mb-2 line-clamp-1">
                      {meeting.description}
                    </p>
                  )}

                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{formatMeetingDate(meeting.scheduledDate)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{meeting.invitedMembers?.length || 0} participant(s)</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 ml-4">
                  {responseStatus && !isOrganizer && (
                    <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusColor(responseStatus)}`}>
                      {getStatusLabel(responseStatus)}
                    </span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(meeting.meetingLink, '_blank');
                    }}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-xl transition-all duration-200 flex items-center gap-1 shadow-md hover:shadow-lg"
                  >
                    <Video className="h-4 w-4" />
                    Rejoindre
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Composant pour afficher les notes importantes dans l'Overview
const ImportantNotesSection: React.FC<{ projectId: string; user: any; notes?: any[] }> = ({ projectId, user, notes: preloaded }) => {
  const [importantNotes, setImportantNotes] = useState<any[]>(preloaded ?? []);
  const [loading, setLoading] = useState(preloaded === undefined);
  const navigate = useNavigate();
  const API_BASE = API_BASE_URL;

  useEffect(() => {
    if (preloaded !== undefined) {
      setImportantNotes(preloaded);
      setLoading(false);
      return;
    }
    const fetchImportantNotes = async () => {
      if (!projectId || !user?._id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE}/notes/project/${projectId}/important`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setImportantNotes(response.data.data);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des notes importantes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchImportantNotes();
  }, [projectId, user, preloaded]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (importantNotes.length === 0) {
    return null; // Ne rien afficher s'il n'y a pas de notes importantes
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-yellow-100 flex items-center justify-center">
            <Star className="h-5 w-5 text-yellow-600 fill-yellow-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <div className="h-1 w-1 rounded-full bg-amber-400"></div>
              Notes importantes
            </h2>
            <p className="text-sm text-gray-500">Notes marked as important by the team</p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/projects/${projectId}`, { state: { activeTab: 'notes' } })}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
        >
          Voir toutes les notes
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {importantNotes.map((note) => (
          <div
            key={note._id}
            onClick={() => navigate(`/projects/${projectId}`, { state: { activeTab: 'notes', noteId: note._id } })}
            className="p-4 border border-gray-200/60 rounded-xl hover:border-blue-300 hover:shadow-lg transition-all duration-200 cursor-pointer group bg-white"
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                {note.title || 'Sans titre'}
              </h3>
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0 ml-2" />
            </div>
            <p className="text-sm text-gray-600 line-clamp-3 mb-3">
              {note.content.substring(0, 150)}
              {note.content.length > 150 ? '...' : ''}
            </p>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <User className="h-3 w-3" />
                <span>{note.createdBy?.name || 'Inconnu'}</span>
              </div>
              <span>
                {new Date(note.updatedAt).toLocaleDateString('en-US', {
                  day: 'numeric',
                  month: 'short'
                })}
              </span>
            </div>
            {note.tags && note.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {note.tags.slice(0, 3).map((tag: string, idx: number) => (
                  <span
                    key={idx}
                    className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const ProjectInterfacePage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const user = useSelector((state: RootState) => selectUser(state));
  const navigate = useNavigate();
  const location = useLocation();

  const [project, setProject] = useState<ProjectData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [teamFormData, setTeamFormData] = useState<TeamFormData>({
    name: '',
    description: '',
    memberEmails: []
  });
  const [selectedTeam, setSelectedTeam] = useState<TeamData | null>(null);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    start: '',
    end: '',
    type: 'meeting',
    isPublic: true
  });
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'list' | 'board' | 'timeline' | 'dashboard' | 'workflow' | 'messages' | 'votes' | 'planning-poker' | 'notes' | 'collaboration' | 'meetings' | 'equipe'>('overview');
  const [taskView, setTaskView] = useState<'tasks' | 'list' | 'board' | 'timeline'>('list');
  const [collaborationDropdownOpen, setCollaborationDropdownOpen] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [tempDescription, setTempDescription] = useState('');
  
  // Task creation modal state
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [selectedTeamForTask, setSelectedTeamForTask] = useState<string>('');
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    deadline: '',
    assignee: '',
    team: ''
  });

  // Task submission and validation states
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [taskDetailTab, setTaskDetailTab] = useState<'details' | 'files' | 'validation' | 'feedback'>('details');
  const [taskFiles, setTaskFiles] = useState<Record<string, any[]>>({});
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({});
  const [taskSubmissions, setTaskSubmissions] = useState<Record<string, any>>({});
  const [taskValidations, setTaskValidations] = useState<Record<string, any[]>>({});
  const [taskValidationVotes, setTaskValidationVotes] = useState<Record<string, any>>({});
  const [userValidationVote, setUserValidationVote] = useState<Record<string, any>>({});
  const [validationFormData, setValidationFormData] = useState({
    status: 'approved' as 'approved' | 'revision-requested' | 'rejected',
    feedback: '',
    pointsToFix: [] as string[]
  });

  // Collaboration states (for Votes tab)
  const [collaborationActiveTab, setCollaborationActiveTab] = useState<'votes' | 'tasks' | 'planning-poker' | 'files-collab'>('votes');
  const [collaborationVotes, setCollaborationVotes] = useState<any[]>([]);
  const [collaborationTasks, setCollaborationTasks] = useState<any[]>([]);
  const [collaborationVotesLoading, setCollaborationVotesLoading] = useState(false);
  const [collaborationTasksLoading, setCollaborationTasksLoading] = useState(false);
  
  // Project member roles state
  const [projectMemberRoles, setProjectMemberRoles] = useState<any[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [showCreateVoteModal, setShowCreateVoteModal] = useState(false);
  const [showCreateCollaborationTaskModal, setShowCreateCollaborationTaskModal] = useState(false);
  const [selectedVote, setSelectedVote] = useState<any | null>(null);
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [selectedVoteOptions, setSelectedVoteOptions] = useState<number[]>([]);
  const [voteFormData, setVoteFormData] = useState({
    title: '',
    description: '',
    type: 'simple' as 'simple' | 'multiple',
    options: [{ text: '', order: 0 }, { text: '', order: 1 }],
    endDate: '',
    resultsVisibility: 'after_close' as 'hidden' | 'realtime' | 'after_close',
    allowVoteModification: true,
    isAnonymous: false
  });
  const [collaborationTaskFormData, setCollaborationTaskFormData] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    deadline: '',
    tags: [] as string[],
    team: ''
  });

  // Dashboard state
  const [dashboardTasks, setDashboardTasks] = useState<any[]>([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const API_BASE = API_BASE_URL;

type RoleCardTheme = {
  card: string;
  badge: string;
  initials: string;
  icon: string;
};

const ownerRoleTheme: RoleCardTheme = {
  card: 'bg-gradient-to-br from-orange-50 via-white to-white border border-orange-200 shadow-lg shadow-orange-100',
  badge: 'bg-orange-100 text-orange-700 border border-orange-200',
  initials: 'bg-gradient-to-br from-orange-500 to-pink-500',
  icon: 'text-orange-500'
};

const collaboratorThemes: RoleCardTheme[] = [
  {
    card: 'bg-white border border-blue-100 shadow-sm hover:border-blue-200',
    badge: 'bg-blue-100 text-blue-700 border border-blue-200',
    initials: 'bg-gradient-to-br from-blue-500 to-indigo-500',
    icon: 'text-blue-500'
  },
  {
    card: 'bg-white border border-emerald-100 shadow-sm hover:border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    initials: 'bg-gradient-to-br from-emerald-500 to-teal-500',
    icon: 'text-emerald-500'
  },
  {
    card: 'bg-white border border-purple-100 shadow-sm hover:border-purple-200',
    badge: 'bg-purple-100 text-purple-700 border border-purple-200',
    initials: 'bg-gradient-to-br from-purple-500 to-fuchsia-500',
    icon: 'text-purple-500'
  },
  {
    card: 'bg-white border border-rose-100 shadow-sm hover:border-rose-200',
    badge: 'bg-rose-100 text-rose-700 border border-rose-200',
    initials: 'bg-gradient-to-br from-rose-500 to-amber-500',
    icon: 'text-rose-500'
  }
];

const getCollaboratorTheme = (seed?: string): RoleCardTheme => {
  if (!seed) return collaboratorThemes[0];
  const parsed = parseInt(seed.slice(-2), 16);
  const index = Number.isNaN(parsed) ? 0 : parsed % collaboratorThemes.length;
  return collaboratorThemes[index];
};

const getInitials = (name?: string) => {
  if (!name) return '??';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return name.slice(0, 2).toUpperCase();
  return parts.slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('');
};



  // Sync activeTab with URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    
    // Si pas de paramètre tab, c'est Overview par défaut
    if (!tab) {
      setActiveTab('overview');
      return;
    }
    
    if (['overview', 'tasks', 'list', 'board', 'timeline', 'dashboard', 'workflow', 'messages', 'notes', 'votes', 'planning-poker', 'collaboration', 'meetings', 'equipe'].includes(tab)) {
      // Tous les affichages de tâches vivent dans l'onglet Tasks (view switcher)
      if (tab === 'tasks') {
        setActiveTab('tasks');
        const view = params.get('view');
        setTaskView(view === 'tasks' || view === 'list' || view === 'board' || view === 'timeline' ? view : 'list');
      } else if (tab === 'list' || tab === 'board' || tab === 'timeline') {
        // Anciennes URLs ?tab=list|board|timeline -> nouvel onglet Tasks
        setActiveTab('tasks');
        setTaskView(tab);
      // Si c'est 'collaboration', afficher tâches (via navbar) ou fichiers collaboratifs par défaut
      } else if (tab === 'collaboration') {
        const subtab = params.get('subtab');
        if (subtab === 'tasks') {
          setActiveTab('tasks');
          setTaskView('tasks');
        } else {
          setActiveTab('votes');
          setCollaborationActiveTab('files-collab');
        }
      } else if (tab === 'planning-poker') {
        setActiveTab('planning-poker');
        setCollaborationActiveTab('planning-poker');
      } else {
        setActiveTab(tab as any);
      }
    }
  }, [location.search, location.pathname]);

  // Fetch collaboration data when votes tab or tasks cards view is active
  useEffect(() => {
    if (projectId && user?._id && (activeTab === 'votes' || (activeTab === 'tasks' && taskView === 'tasks'))) {
      fetchCollaborationVotes();
      fetchCollaborationTasks();
    }
  }, [activeTab, taskView, projectId, user?._id]);

  // Fetch dashboard data when dashboard tab is active
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!projectId || !user?._id) return;
      setDashboardLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/tasks/${projectId}?userId=${user._id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const data = res.data?.data;
        const tasks = Array.isArray(data?.tasks) ? data.tasks : (Array.isArray(data) ? data : []);
        setDashboardTasks(tasks);
      } catch (err) {
        setDashboardTasks([]);
      } finally {
        setDashboardLoading(false);
      }
    };
    if (activeTab === 'dashboard') {
      fetchDashboardData();
    }
  }, [activeTab, projectId, user?._id]);



  // Fetch project member roles
  const fetchProjectMemberRoles = async () => {
    if (!projectId) return;
    setRolesLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/projects/${projectId}/member-roles`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setProjectMemberRoles(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching project member roles:', err);
      setProjectMemberRoles([]);
    } finally {
      setRolesLoading(false);
    }
  };

  // Fetch project member roles when overview tab is active
  useEffect(() => {
    if (activeTab === 'overview' && projectId) {
      fetchProjectMemberRoles();
    }
  }, [activeTab, projectId]);

  // Fetch collaboration votes
  const fetchCollaborationVotes = async () => {
    if (!projectId) return;
    setCollaborationVotesLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/votes/project/${projectId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCollaborationVotes(res.data.data || []);
    } catch (err: any) {
      console.error('Erreur fetch votes:', err);
    } finally {
      setCollaborationVotesLoading(false);
    }
  };

  // Fetch collaboration tasks
  const fetchCollaborationTasks = async () => {
    if (!projectId || !user?._id) return;
    setCollaborationTasksLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/tasks/${projectId}?userId=${user._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const responseData = res.data?.data;
      const fetchedTasks = Array.isArray(responseData?.tasks) ? responseData.tasks : (Array.isArray(responseData) ? responseData : []);
      setCollaborationTasks(fetchedTasks);
      
    } catch (err: any) {
      console.error('Erreur fetch tasks:', err);
    } finally {
      setCollaborationTasksLoading(false);
    }
  };


  // Create vote
  const handleCreateVote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !user?._id) return;

    if (!voteFormData.title.trim()) {
      return;
    }
    if (voteFormData.options.length < 2) {
      return;
    }
    if (voteFormData.options.some(opt => !opt.text.trim())) {
      return;
    }
    if (!voteFormData.endDate) {
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/votes`,
        {
          ...voteFormData,
          projectId,
          options: voteFormData.options.map((opt, idx) => ({ text: opt.text, order: idx }))
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        await fetchCollaborationVotes();
        setShowCreateVoteModal(false);
        setVoteFormData({
          title: '',
          description: '',
          type: 'simple',
          options: [{ text: '', order: 0 }, { text: '', order: 1 }],
          endDate: '',
          resultsVisibility: 'after_close',
          allowVoteModification: true,
          isAnonymous: false
        });
      }
    } catch (err: any) {
      console.error('Erreur création vote:', err);
    }
  };

  // Submit vote
  const handleSubmitVote = async () => {
    if (!selectedVote || selectedVoteOptions.length === 0) return;
    try {
      await axios.post(
        `${API_BASE}/votes/${selectedVote._id}/vote`,
        { selectedOptions: selectedVoteOptions.map(idx => ({ optionIndex: idx })) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      await fetchCollaborationVotes();
      setShowVoteModal(false);
      setSelectedVote(null);
      setSelectedVoteOptions([]);
    } catch (err: any) {
      console.error('Erreur soumission vote:', err);
    }
  };

  // Create collaboration task
  const handleCreateCollaborationTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !user?._id) return;

    if (!collaborationTaskFormData.title.trim()) {
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/tasks`,
        {
          ...collaborationTaskFormData,
          project: projectId,
          team: collaborationTaskFormData.team || undefined,
          status: 'todo'
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        await fetchCollaborationTasks();
        setShowCreateCollaborationTaskModal(false);
        setCollaborationTaskFormData({
          title: '',
          description: '',
          priority: 'medium',
          deadline: '',
          tags: [],
          team: ''
        });
      }
    } catch (err: any) {
      console.error('Erreur création tâche:', err);
    }
  };


  const formatCollaborationDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isVoteActive = (vote: any) => {
    return vote.status === 'active' && new Date(vote.endDate) > new Date();
  };

  const canShowResults = (vote: any) => {
    if (vote.resultsVisibility === 'realtime') return true;
    if (vote.resultsVisibility === 'after_close' && vote.status === 'closed') return true;
    return false;
  };

  const hasValidResults = (vote: any) => {
    return vote.results && vote.results.optionResults && vote.results.optionResults.length > 0;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'bg-green-100 text-green-700';
      case 'in-progress': return 'bg-blue-100 text-blue-700';
      case 'review': return 'bg-purple-100 text-purple-700';
      case 'blocked': return 'bg-red-100 text-red-700';
      case 'archived': return 'bg-gray-100 text-gray-700';
      case 'submitted': return 'bg-yellow-100 text-yellow-700';
      case 'under-review': return 'bg-orange-100 text-orange-700';
      case 'revision-requested': return 'bg-pink-100 text-pink-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Open task detail modal
  const openTaskDetailModal = async (task: any) => {
    setSelectedTask(task);
    setTaskDetailTab('details');
    setShowTaskDetailModal(true);
    await fetchTaskFiles(task._id);
    await fetchTaskSubmission(task._id); // Cette fonction récupère maintenant aussi les votes si soumis
    await fetchTaskValidations(task._id);
  };

  // Récupérer automatiquement la soumission et les votes quand l'onglet validation est affiché
  useEffect(() => {
    if (taskDetailTab === 'validation' && selectedTask?._id) {
      // Toujours récupérer la soumission pour être sûr qu'elle est à jour
      const loadSubmissionAndVotes = async () => {
        try {
          // Récupérer la soumission
          const submissionRes = await axios.get(`${API_BASE}/tasks/${selectedTask._id}/submission`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          
          if (submissionRes.data.success) {
            const submission = submissionRes.data.data;
            setTaskSubmissions(prev => ({ ...prev, [selectedTask._id]: submission }));
            
            // Si la soumission est en statut 'submitted', récupérer les votes
            if (submission && (submission.status === 'submitted' || submission.status === 'under-review')) {
              await fetchTaskValidationVotes(selectedTask._id);
            }
          }
        } catch (err: any) {
          // Si pas de soumission, vérifier quand même si la tâche est soumise
          if (selectedTask.status === 'submitted' || selectedTask.status === 'under-review') {
            await fetchTaskValidationVotes(selectedTask._id);
          }
        }
      };
      loadSubmissionAndVotes();
    }
  }, [taskDetailTab, selectedTask?._id]);

  // Fetch task files
  const fetchTaskFiles = async (taskId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/tasks/${taskId}/files`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setTaskFiles(prev => ({ ...prev, [taskId]: res.data.data || [] }));
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Erreur récupération fichiers:', err);
      }
      setTaskFiles(prev => ({ ...prev, [taskId]: [] }));
    }
  };

  // Fetch task submission
  const fetchTaskSubmission = async (taskId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/tasks/${taskId}/submission`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setTaskSubmissions(prev => ({ ...prev, [taskId]: res.data.data }));
        // Si la soumission est en statut 'submitted', récupérer les votes automatiquement
        if (res.data.data && (res.data.data.status === 'submitted' || res.data.data.status === 'under-review')) {
          await fetchTaskValidationVotes(taskId);
        }
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Erreur récupération soumission:', err);
      }
    }
  };

  // Fetch task validations
  const fetchTaskValidations = async (taskId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/tasks/${taskId}/validations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setTaskValidations(prev => ({ ...prev, [taskId]: res.data.data || [] }));
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Erreur récupération validations:', err);
      }
      setTaskValidations(prev => ({ ...prev, [taskId]: [] }));
    }
  };

  // Fetch task validation votes
  const fetchTaskValidationVotes = async (taskId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/tasks/${taskId}/validation-votes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setTaskValidationVotes(prev => ({ ...prev, [taskId]: res.data.voteResults || {} }));
        
        // Trouver le vote de l'utilisateur actuel
        const userVote = res.data.data.find((v: any) => 
          v.voter && (v.voter._id === user?._id || v.voter === user?._id)
        );
        if (userVote) {
          setUserValidationVote(prev => ({ ...prev, [taskId]: userVote }));
        } else {
          setUserValidationVote(prev => ({ ...prev, [taskId]: null }));
        }
        
        // Si la tâche a été validée automatiquement, rafraîchir toutes les données
        if (res.data.voteResults?.autoValidated) {
          // Rafraîchir les validations et la soumission
          await fetchTaskValidations(taskId);
          await fetchTaskSubmission(taskId);
          
          // Rafraîchir la liste des tâches dans TaskListView
          window.dispatchEvent(new CustomEvent('refreshTaskList'));
          
          // Afficher un message informatif
          console.log('✅ Tâche validée automatiquement par les votes de l\'équipe');
        }
      }
    } catch (err: any) {
      console.error('Erreur récupération votes de validation:', err);
      setTaskValidationVotes(prev => ({ ...prev, [taskId]: {} }));
    }
  };

  // Vote on task validation
  const handleVoteOnValidation = async (taskId: string, vote: 'valid' | 'invalid', comment?: string) => {
    try {
      const res = await axios.post(
        `${API_BASE}/tasks/${taskId}/validation-vote`,
        { vote, comment },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        await fetchTaskValidationVotes(taskId);
        
        // Si la tâche a été validée automatiquement, rafraîchir toutes les données
        if (res.data.voteResults?.autoValidated) {
          // Rafraîchir les validations et la soumission
          await fetchTaskValidations(taskId);
          await fetchTaskSubmission(taskId);
          
          // Rafraîchir la liste des tâches dans TaskListView
          window.dispatchEvent(new CustomEvent('refreshTaskList'));
          
          const voteResults = res.data.voteResults;
          alert(`✅ Vote recorded. The task has been validated automatically!\n\n` +
                `Votes valides: ${voteResults.validVotes}\n` +
                `Votes non valides: ${voteResults.invalidVotes}\n` +
                `Total votes: ${voteResults.totalVotes} / ${voteResults.teamSize} membres\n` +
                `Minimum requis: ${voteResults.minRequiredVotes} votes`);
        } else {
          const voteResults = res.data.voteResults;
          if (voteResults) {
            const remaining = voteResults.minRequiredVotes - voteResults.totalVotes;
            if (remaining > 0) {
              alert(`Vote recorded successfully.\n\n` +
                    `Votes actuels: ${voteResults.totalVotes} / ${voteResults.minRequiredVotes} minimum requis\n` +
                    `${remaining} vote(s) still needed for automatic validation.`);
            } else {
              alert('Vote recorded successfully');
            }
          } else {
            alert('Vote recorded successfully');
          }
        }
      }
    } catch (err: any) {
      console.error('Erreur vote validation:', err);
      alert(err.response?.data?.error || 'Erreur lors de l\'enregistrement du vote');
    }
  };

  // Upload task files
  const handleUploadFiles = async (taskId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    setUploadingFiles(prev => ({ ...prev, [taskId]: true }));
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });

      const res = await axios.post(
        `${API_BASE}/tasks/${taskId}/files`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (res.data.success) {
        await fetchTaskFiles(taskId);
      }
    } catch (err: any) {
      console.error('Erreur upload fichiers:', err);
      alert(err.response?.data?.error || 'Erreur lors de l\'upload des fichiers');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [taskId]: false }));
    }
  };

  // Submit task for validation
  const handleSubmitTask = async (taskId: string, comment?: string) => {
    try {
      const res = await axios.post(
        `${API_BASE}/tasks/${taskId}/submit`,
        { comment },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        // Trigger refresh in TaskListView
        window.dispatchEvent(new CustomEvent('refreshTaskList'));
        await fetchTaskSubmission(taskId);
        // Récupérer les votes de validation après soumission
        await fetchTaskValidationVotes(taskId);
        alert('Task submitted for validation');
      }
    } catch (err: any) {
      console.error('Erreur soumission:', err);
      alert(err.response?.data?.error || 'Erreur lors de la soumission');
    }
  };

  // Validate task (creator only)
  const handleValidateTask = async (taskId: string) => {
    if (!isCreator) {
      alert('Only the project creator can validate tasks');
      return;
    }

    try {
      // Le créateur peut toujours valider, même si les votes ne sont pas favorables
      // On envoie forceValidation: true pour permettre la validation
      const res = await axios.post(
        `${API_BASE}/tasks/${taskId}/validate`,
        {
          status: validationFormData.status,
          feedback: validationFormData.feedback,
          pointsToFix: validationFormData.pointsToFix,
          forceValidation: true // Le créateur peut forcer la validation
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        // Trigger refresh in TaskListView
        window.dispatchEvent(new CustomEvent('refreshTaskList'));
        await fetchTaskValidations(taskId);
        await fetchTaskValidationVotes(taskId);
        setValidationFormData({
          status: 'approved',
          feedback: '',
          pointsToFix: []
        });
        alert('Validation saved');
      }
    } catch (err: any) {
      console.error('Erreur validation:', err);
      const errorMessage = err.response?.data?.error || 'Erreur lors de la validation';
      alert(errorMessage);
      
      // Si l'erreur contient des informations sur les votes, mettre à jour l'affichage
      if (err.response?.data?.voteResults) {
        setTaskValidationVotes(prev => ({ 
          ...prev, 
          [taskId]: err.response.data.voteResults 
        }));
      }
    }
  };

  // Delete task file
  const handleDeleteFile = async (taskId: string, fileId: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      const res = await axios.delete(
        `${API_BASE}/tasks/${taskId}/files/${fileId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        await fetchTaskFiles(taskId);
      }
    } catch (err: any) {
      console.error('Erreur suppression fichier:', err);
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // Download task file
  const handleDownloadFile = async (taskId: string, fileId: string, fileName: string) => {
    try {
      const res = await axios.get(
        `${API_BASE}/tasks/${taskId}/files/${fileId}/download`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      console.error('Erreur téléchargement:', err);
      alert(err.response?.data?.error || 'Download error');
    }
  };

  // Fetch project details
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!projectId || !user?._id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_BASE}/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const projectData = res.data.data;
        setProject(projectData);
        setTempDescription(projectData.description || '');
        setFormData({
          title: projectData.title,
          description: projectData.description || '',
          start: new Date(projectData.start).toISOString().slice(0, 16),
          end: new Date(projectData.end).toISOString().slice(0, 16),
          type: projectData.type,
          isPublic: projectData.isPublic
        });
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error loading details');
      } finally {
        setLoading(false);
      }
    };
    fetchProjectDetails();
  }, [projectId, user?._id]);

  // Gérer le changement d'onglet depuis location.state
  useEffect(() => {
    const state = location.state as any;
    if (state?.activeTab) {
      setActiveTab(state.activeTab);
      // Nettoyer le state pour éviter de réactiver l'onglet à chaque re-render
      window.history.replaceState({ ...state, activeTab: undefined }, '');
    }
  }, [location.state]);

  // Auto-expand teams with pending join requests for team owners
  useEffect(() => {
    if (!project || !user?._id || !project.teams) return;
    
    // Find teams where user is creator and has pending join requests
    const teamsWithPendingRequests = project.teams.filter(team => {
      const isTeamCreator = team.creator._id === user._id;
      const pendingRequests = team.joinRequests?.filter(r => r.status === 'pending') || [];
      return isTeamCreator && pendingRequests.length > 0;
    });

    // Auto-expand the first team with pending requests
    // Check if the currently expanded team still has pending requests, if not, expand the first one with requests
    if (teamsWithPendingRequests.length > 0) {
      const currentlyExpandedTeam = project.teams.find(t => t._id === expandedTeamId);
      const expandedTeamHasRequests = currentlyExpandedTeam && 
        currentlyExpandedTeam.creator._id === user._id &&
        (currentlyExpandedTeam.joinRequests?.filter(r => r.status === 'pending') || []).length > 0;
      
      // If no team is expanded or the expanded team doesn't have requests anymore, expand the first one with requests
      if (!expandedTeamId || !expandedTeamHasRequests) {
        setExpandedTeamId(teamsWithPendingRequests[0]._id);
      }
    }
  }, [project, user?._id, expandedTeamId]);

  // Refresh project after team operations
  const refreshProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API_BASE}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProject(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors du rechargement');
    }
  }, [projectId]);

  // Update project
  const handleUpdateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?._id || !projectId) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = { ...formData, creatorId: user._id };
      const res = await axios.put(`${API_BASE}/projects/${projectId}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProject(res.data.data);
      setShowEditModal(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la modification');
    } finally {
      setSubmitting(false);
    }
  };

  // Create team
  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?._id || !projectId) return;
    if (teamFormData.memberEmails.length === 0) {
      setError('Au moins un email membre est requis');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = { 
        projectId,
        name: teamFormData.name,
        description: teamFormData.description,
        memberEmails: teamFormData.memberEmails,
        creatorId: user._id 
      };
      const res = await axios.post(`${API_BASE}/teams`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProject(res.data.data);
      setShowCreateTeamModal(false);
      setTeamFormData({ name: '', description: '', memberEmails: [] });
      setNewEmailInput('');
      setError(null);
      setSuccessMessage('Team created and invitations sent successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error creating team');
    } finally {
      setSubmitting(false);
    }
  };
const handleJoinRequest = async (teamId: string) => {
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/teams/${teamId}/join-request`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSuccessMessage('Request sent! Awaiting approval.');
      await refreshProject();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Impossible d\'envoyer la demande');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Edit team
  const handleEditTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?._id || !selectedTeam?._id || !projectId) return;
    if (teamFormData.memberEmails.length === 0) {
      setError('Au moins un email membre est requis');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = { 
        linkedProjectId: projectId,
        name: teamFormData.name,
        description: teamFormData.description,
        memberEmails: teamFormData.memberEmails, // For new invites
        creatorId: user._id 
      };
      // Assuming backend supports PUT /api/teams/:id for updates
      const res = await axios.put(`${API_BASE}/teams/${selectedTeam._id}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      await refreshProject();
      setShowEditTeamModal(false);
      setTeamFormData({ name: '', description: '', memberEmails: [] });
      setNewEmailInput('');
      setError(null);
      setSuccessMessage('Team updated successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error updating team');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete team
  const requestDeleteTeam = (teamId: string) => {
    setConfirmAction(() => async () => {
      setSubmitting(true);
      try {
        await axios.delete(`${API_BASE}/teams/${teamId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        await refreshProject();
        setSuccessMessage('Team deleted successfully!');
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Erreur lors de la suppression');
      } finally {
        setSubmitting(false);
      }
    });
    setConfirmMessage('Are you sure you want to delete this team? This action is irreversible.');
    setShowConfirmModal(true);
  };

  // Add member to team (via invite)
  const handleAddMemberToTeam = async (teamId: string, email: string) => {
    if (!user?._id) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        memberEmails: [email],
        creatorId: user._id
      };
      const res = await axios.put(`${API_BASE}/teams/${teamId}/invite`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Si le backend retourne le projet mis à jour, l'utiliser directement
      if (res.data.data && res.data.data.teams) {
        setProject(res.data.data);
      } else {
        // Sinon, recharger le projet
        await refreshProject();
      }
      
      setSuccessMessage(`Invitation sent to ${email}!`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de l\'ajout du membre';
      setError(errorMessage);
      console.error('Erreur lors de l\'ajout du membre:', err.response?.data || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Remove member from team
  const handleRemoveMemberFromTeam = async (teamId: string, memberId: string) => {
    if (!user?._id) return;
    
    // Trouver l'équipe pour vérifier si le membre est le créateur
    const team = project?.teams?.find(t => t._id === teamId);
    if (team && team.creator._id === memberId) {
      setError('The team creator cannot be removed');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.delete(`${API_BASE}/teams/${teamId}/members/${memberId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      await refreshProject();
      setSuccessMessage('Member removed from the team!');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression du membre');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete project
  const requestDeleteProject = () => {
    setConfirmAction(() => () => {
      setSubmitting(true);
      axios.delete(`${API_BASE}/projects/${projectId}`, {
        data: { creatorId: user!._id },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(() => {
        navigate('/projects');
      }).catch((err: any) => {
        setError(err.response?.data?.error || 'Erreur lors de la suppression');
        setSubmitting(false);
      });
    });
    setConfirmMessage('Are you sure you want to delete this project? This action is irreversible.');
    setShowConfirmModal(true);
  };

  // Handle confirm
  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction();
    }
    setShowConfirmModal(false);
  };

  // Form handlers
  const handleFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type: inputType } = e.target;
    if (inputType === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleTeamFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTeamFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // Load team data for edit
  const handleEditTeamClick = (team: TeamData) => {
    setSelectedTeam(team);
    setTeamFormData({
      name: team.name,
      description: team.description || '',
      memberEmails: [] // New invites only
    });
    setShowEditTeamModal(true);
  };

  // Ajouter un email à la liste (for create/edit)
  const handleAddEmail = () => {
    const email = newEmailInput.trim();
    if (!email) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }
    
    if (teamFormData.memberEmails.includes(email)) {
      setError('This email is already in the list');
      return;
    }
    
    setTeamFormData(prev => ({
      ...prev,
      memberEmails: [...prev.memberEmails, email]
    }));
    setNewEmailInput('');
    setError(null);
  };

  // Supprimer un email de la liste
  const handleRemoveEmail = (emailToRemove: string) => {
    setTeamFormData(prev => ({
      ...prev,
      memberEmails: prev.memberEmails.filter(email => email !== emailToRemove)
    }));
  };

  // Gérer la touche Entrée dans le champ email
  const handleEmailInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  // Toggle expand team row
  const toggleExpandTeam = (teamId: string) => {
    setExpandedTeamId(expandedTeamId === teamId ? null : teamId);
  };

  // Type badge colors
  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      meeting: 'bg-blue-100 text-blue-800 border-blue-300',
      workshop: 'bg-purple-100 text-purple-800 border-purple-300',
      deadline: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      social: 'bg-green-100 text-green-800 border-green-300'
    };
    return colors[type] || colors.meeting;
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      meeting: 'Meeting',
      workshop: 'Travail',
      deadline: 'Deadline',
      social: 'Personal'
    };
    return labels[type] || type;
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const calculateDuration = () => {
    if (!project) return '';
    const start = new Date(project.start);
    const end = new Date(project.end);
    const diff = end.getTime() - start.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) {
      return `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`;
    }
    return `${minutes}min`;
  };


  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="h-12 w-12 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-600 text-lg">Loading project details...</p>
      </div>
    );
  }

  // Error state
  if (error && !project) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <AlertTriangle className="h-16 w-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button
          onClick={() => navigate('/projects')}
          className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux projets
        </button>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <FileText className="h-16 w-16 text-gray-400 mb-4" />
        <p className="text-gray-600 text-lg">Project not found.</p>
      </div>
    );
  }

  const isCreator = user?._id === project.creator._id;

  // Build role cards from project member roles
  const roleCards: {
    id: string;
    name: string;
    email: string;
    badge: string;
    description: string;
    theme: RoleCardTheme;
  }[] = [];

  // Create a map of userId -> role for quick lookup
  const rolesMap = new Map();
  projectMemberRoles.forEach((roleData: any) => {
    if (roleData.userId && roleData.userId._id) {
      rolesMap.set(roleData.userId._id.toString(), {
        role: roleData.role,
        user: roleData.userId
      });
    }
  });

  // Add creator as Owner (always first)
  if (project.creator) {
    const creatorId = project.creator._id.toString();
    const creatorRole = rolesMap.get(creatorId);
    roleCards.push({
      id: creatorId,
      name: project.creator.name,
      email: project.creator.email,
      badge: 'Owner',
      description: 'Pilots the project, teams and critical settings',
      theme: ownerRoleTheme
    });
    // Remove from map to avoid duplicates
    rolesMap.delete(creatorId);
  }

  // Add other members with their assigned roles
  rolesMap.forEach((roleData: any, userId: string) => {
    const user = roleData.user;
    const role = roleData.role || 'Collaborator';
    const theme = getCollaboratorTheme(userId || user.email || '');
    
    // Determine description based on role
    let description = 'Participates in tasks, shares updates and follows the roadmap';
    if (role === 'Décideur') {
      description = 'Makes strategic decisions and validates project directions';
    } else if (role === 'Coordinateur') {
      description = 'Coordinates activities and ensures communication between members';
    } else if (role === 'Chercheur') {
      description = 'Conducts research and analysis necessary for the project';
    } else if (role === 'Partenaire') {
      description = 'External partner contributing to the project';
    } else if (role === 'Observateur') {
      description = 'Observes activities without active participation';
    }

    roleCards.push({
      id: userId,
      name: user.name || user.email || 'Utilisateur',
      email: user.email || '',
      badge: role,
      description,
      theme
    });
  });

  // Also add members from teams who don't have a role assigned yet
  const activeTeam = project.teams && project.teams.length > 0 ? project.teams[0] : null;
  if (activeTeam && activeTeam.members) {
    activeTeam.members.forEach((member: any) => {
      const memberId = typeof member === 'object' ? member._id?.toString() : member?.toString();
      if (memberId && !rolesMap.has(memberId) && memberId !== project.creator?._id?.toString()) {
        const memberName = typeof member === 'object' ? member.name : '';
        const memberEmail = typeof member === 'object' ? member.email : '';
        const theme = getCollaboratorTheme(memberId || memberEmail || '');
        roleCards.push({
          id: memberId,
          name: memberName || memberEmail || 'Utilisateur',
          email: memberEmail || '',
      badge: 'Collaborator',
      description: 'Participates in tasks, shares updates and follows the roadmap',
      theme
    });
      }
  });
  }

  const hasRoleCollaborators = roleCards.length > 1;

  const handleCopyCode = () => {
    if (project?.accessCode) {
      navigator.clipboard.writeText(project.accessCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
const handleAcceptJoinRequest = async (teamId: string, requestId: string) => {
  try {
    await axios.put(`${API_BASE}/teams/${teamId}/join-request/${requestId}/accept`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });
    setSuccessMessage('Member added to the team!');
    await refreshProject();
  } catch (err: any) {
    setError(err.response?.data?.error || 'Erreur lors de l\'acceptation');
  }
};

const handleRejectJoinRequest = async (teamId: string, requestId: string) => {
  try {
    await axios.put(`${API_BASE}/teams/${teamId}/join-request/${requestId}/reject`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });
    setSuccessMessage('Request declined');
    await refreshProject();
  } catch (err: any) {
    setError(err.response?.data?.error || 'Erreur lors du refus');
  }
};
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Shared Project Navbar */}
      <ProjectNavbar
        project={project}
        projectId={projectId || ''}
        isCreator={isCreator}
        onEdit={() => setShowEditModal(true)}
        onDelete={requestDeleteProject}
      />

      {/* Success/Error Messages */}
      {(successMessage || error) && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-4">
            {/* Success Banner */}
            {successMessage && (
              <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4 rounded-lg flex items-start">
                <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800">{successMessage}</p>
                </div>
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="ml-4 text-green-500 hover:text-green-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}

            {/* Error Banner */}
            {error && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="ml-4 text-red-500 hover:text-red-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Project Description Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="ml-[52px]">
            {editingDescription ? (
              <div className="space-y-3">
                <textarea
                  value={tempDescription}
                  onChange={(e) => setTempDescription(e.target.value)}
                  placeholder="What's this project about?"
                  className="w-full min-h-[80px] px-4 py-3 border border-gray-200/60 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 text-gray-900 placeholder-gray-400 resize-none shadow-sm focus:shadow-md transition-all duration-200"
                  autoFocus
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      try {
                        const payload = { ...formData, description: tempDescription, creatorId: user!._id };
                        const res = await axios.put(`${API_BASE}/projects/${projectId}`, payload, {
                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                        });
                        setProject(res.data.data);
                        setEditingDescription(false);
                        setSuccessMessage('Description updated successfully');
                      } catch (err: any) {
                        setError(err.response?.data?.error || 'Error updating');
                      }
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setTempDescription(project.description || '');
                      setEditingDescription(false);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <div className="text-gray-600 text-sm min-h-[40px] px-0 py-2">
                  {project.description || (
                    <span className="text-gray-400 italic">What's this project about?</span>
                  )}
                </div>
                {isCreator && (
                  <button
                    onClick={() => {
                      setTempDescription(project.description || '');
                      setEditingDescription(true);
                    }}
                    className="absolute top-0 right-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Edit description"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Project Details Grid - Enhanced Design */}
            <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-cyan-400"></div>
                Project details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="group flex items-start space-x-4 p-4 rounded-lg hover:bg-gray-50/50 transition-all duration-200">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-200">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Start date</p>
                    <p className="text-lg font-semibold text-gray-900">{formatDate(project.start)}</p>
                    <p className="text-sm text-gray-600 mt-1">{formatTime(project.start)}</p>
              </div>
            </div>

            <div className="group flex items-start space-x-4 p-4 rounded-lg hover:bg-gray-50/50 transition-all duration-200">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-200">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Date de fin</p>
                    <p className="text-lg font-semibold text-gray-900">{formatDate(project.end)}</p>
                    <p className="text-sm text-gray-600 mt-1">{formatTime(project.end)}</p>
              </div>
            </div>

            <div className="group flex items-start space-x-4 p-4 rounded-lg hover:bg-gray-50/50 transition-all duration-200">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-200">
                  <Clock className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Duration</p>
                <p className="text-lg font-semibold text-gray-900">{calculateDuration()}</p>
              </div>
            </div>

            <div className="group flex items-start space-x-4 p-4 rounded-lg hover:bg-gray-50/50 transition-all duration-200">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-200">
                  <User className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-500 mb-1">Creator</p>
                    <p className="text-lg font-semibold text-gray-900 truncate">{project.creator.name}</p>
                    <p className="text-sm text-gray-600 truncate">{project.creator.email}</p>
              </div>
            </div>
          </div>
        </div>

            {/* Mon Rôle dans ce Projet - Section pour les membres */}
            {user?._id && projectId && (
              <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="h-5 w-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">My Role in this Project</h2>
                </div>
                <ProjectMemberRoleManager
                  projectId={projectId}
                  userId={user._id}
                  memberName={user.name || ''}
                  memberEmail={user.email || ''}
                  memberGlobalRole={user.globalRole}
                  isCreator={isCreator}
                  currentUserId={user._id}
                  initialProjectRole={project?.myRole?.role ?? null}
                  onUpdate={() => {
                    // Rafraîchir les données du projet
                    const fetchProject = async () => {
                      try {
                        const res = await axios.get(`${API_BASE}/projects/${projectId}`, {
                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                        });
                        if (res.data.success) {
                          setProject(res.data.data);
                        }
                      } catch (err) {
                        console.error('Erreur rafraîchissement projet:', err);
                      }
                    };
                    fetchProject();
                  }}
                />
              </div>
            )}

            {/* Notes Importantes Section - Après les détails du projet */}
            <ImportantNotesSection projectId={projectId || ''} user={user} notes={project.importantNotes} />

            {/* Upcoming meetings Section */}
            {project.teams && project.teams.length > 0 && (
              <UpcomingMeetingsSection projectId={projectId || ''} user={user} meetings={project.upcomingMeetings} />
            )}

            {/* Project Roles Section */}
            <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Project roles</h2>
                  <p className="text-sm text-gray-500">Quickly identify who pilots, who builds, and how to invite the next members.</p>
                </div>
                {isCreator && (
                  <button
                    onClick={() => {
                      if (project.teams && project.teams.length > 0) {
                        const team = project.teams[0];
                        setSelectedTeam(team);
                        setShowEditTeamModal(true);
                        setTeamFormData({ name: team.name, description: team.description || '', memberEmails: [] });
                      } else {
                        setShowCreateTeamModal(true);
                        setTeamFormData({ name: '', description: '', memberEmails: [] });
                      }
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200/60 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <Users className="h-4 w-4" />
                    Manage Team
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {isCreator && (
                  <button
                    onClick={() => {
                      if (project.teams && project.teams.length > 0) {
                        const team = project.teams[0];
                        setSelectedTeam(team);
                        setShowEditTeamModal(true);
                        setTeamFormData({ name: team.name, description: team.description || '', memberEmails: [] });
                      } else {
                        setShowCreateTeamModal(true);
                        setTeamFormData({ name: '', description: '', memberEmails: [] });
                      }
                    }}
                    className="min-h-[150px] rounded-2xl border-2 border-dashed border-gray-300 text-left flex flex-col items-start justify-center gap-3 px-5 py-4 hover:border-purple-300 hover:bg-purple-50/40 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-2xl border-2 border-purple-200 flex items-center justify-center text-purple-500">
                      <Plus className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Add a member</p>
                      <p className="text-xs text-gray-500">Quickly invite your collaborators or partners.</p>
                    </div>
                  </button>
                )}

                {roleCards.map((role) => (
                  <div
                    key={role.id}
                    className={`relative rounded-2xl p-5 transition-all duration-200 ${role.theme.card}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-2xl text-white font-semibold flex items-center justify-center shadow-inner ${role.theme.initials}`}>
                        {getInitials(role.name)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-gray-900">{role.name}</p>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${role.theme.badge}`}>
                            {role.badge}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">{role.email}</p>
                        <div className="mt-3 flex items-center gap-2 text-xs text-gray-600">
                          <Shield className={`h-3.5 w-3.5 ${role.theme.icon}`} />
                          <span>{role.description}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {!hasRoleCollaborators && (
                <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-purple-50 border border-dashed border-purple-200 rounded-2xl text-sm text-purple-800">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  <span>Add your first collaborators to share tasks and speed up progress.</span>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'tasks' && projectId && (
          <div className="space-y-4">
            {/* View mode switcher */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="inline-flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
                {([
                  { id: 'tasks', label: 'Cards', icon: FileText },
                  { id: 'list', label: 'List', icon: List },
                  { id: 'board', label: 'Board', icon: Kanban },
                  { id: 'timeline', label: 'Timeline', icon: GanttChart },
                ] as const).map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => {
                      setTaskView(id);
                      navigate(`/projects/${projectId}?tab=tasks&view=${id}`, { replace: true });
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      taskView === id
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>
              {taskView === 'tasks' && (
                <button
                  onClick={() => setShowCreateCollaborationTaskModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <Plus className="h-4 w-4" />
                  Create a task
                </button>
              )}
            </div>

            {/* Tasks — card grid */}
            {taskView === 'tasks' && (
              <div>
                {collaborationTasksLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : collaborationTasks.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No tasks yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {collaborationTasks.map((task: any) => (
                      <div
                        key={task._id}
                        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 flex-1">{task.title}</h3>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                        </div>
                        {task.description && (
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{task.description}</p>
                        )}
                        <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                          <span className={`px-2 py-1 rounded ${getStatusColor(task.status)}`}>
                            {task.status}
                          </span>
                          {task.assignee && (
                            <div className="flex items-center">
                              <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">
                                {task.assignee.name?.[0] || '?'}
                              </div>
                              <span className="ml-1">{task.assignee.name}</span>
                            </div>
                          )}
                          {task.deadline && (
                            <span className="flex items-center">
                              <CalendarIcon className="h-3 w-3 mr-1" />
                              {formatCollaborationDate(task.deadline)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <button
                              onClick={() => openTaskDetailModal(task)}
                              className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 rounded-md text-xs font-medium hover:bg-blue-100 transition-colors"
                              title="View details, files and validation"
                            >
                              <Eye className="h-3 w-3" />
                              Details
                            </button>
                            <button
                              onClick={() => {
                                const teamId = typeof task.team === 'object' ? task.team?._id : task.team;
                                if (teamId) {
                                  navigate(`/projects/${projectId}/team/${teamId}/planning-poker`);
                                } else {
                                  setActiveTab('planning-poker');
                                  setCollaborationActiveTab('planning-poker');
                                  navigate(`/projects/${projectId}?tab=planning-poker`);
                                }
                              }}
                              className="flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 rounded-md text-xs font-medium hover:bg-purple-200 transition-colors"
                            >
                              <Play className="h-3 w-3" />
                              Poker
                            </button>
                            {['submitted', 'under-review'].includes(task.status) ? (
                              <button
                                onClick={() => openTaskDetailModal(task)}
                                className="flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-md text-xs font-medium hover:bg-amber-100 transition-colors"
                                title="View the submitted work"
                              >
                                <Send className="h-3 w-3" />
                                Submission
                              </button>
                            ) : task.assignee && (task.assignee._id === user?._id || task.assignee === user?._id) &&
                              ['todo', 'in-progress', 'revision-requested'].includes(task.status) ? (
                              <button
                                onClick={() => openTaskDetailModal(task)}
                                className="flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs font-medium hover:bg-emerald-100 transition-colors"
                                title="Submit this task for review"
                              >
                                <Send className="h-3 w-3" />
                                Submit
                              </button>
                            ) : null}
                          </div>
                          {task.estimatedDuration && task.estimatedDuration > 0 && (
                            <span className="text-xs text-blue-600 flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {task.estimatedDuration} jour{task.estimatedDuration > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-2">
                          <div
                            className="h-full bg-blue-500"
                            style={{ width: `${task.progress || 0}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* List — table view */}
            {taskView === 'list' && (
              <TaskListView
                projectId={projectId}
                project={project}
                user={user}
                isCreator={isCreator}
                onTaskClick={openTaskDetailModal}
                onSubmitTask={(task) => openTaskDetailModal(task)}
              />
            )}

            {/* Board — kanban (embedded) */}
            {taskView === 'board' && (
              <div className="-mx-2">
                <TeamTasksPage embedded />
              </div>
            )}

            {/* Timeline — gantt (embedded) */}
            {taskView === 'timeline' && (
              <TimelinePage embedded />
            )}
          </div>
        )}

        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Tableau de bord</h2>
                <p className="text-gray-500 mt-1">Vue d'ensemble de l'avancement du projet</p>
              </div>
            </div>

            {dashboardLoading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
              </div>
            ) : (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(() => {
                    const total = dashboardTasks.length;
                    const done = dashboardTasks.filter((t: any) => t.status === 'done').length;
                    const inProgress = dashboardTasks.filter((t: any) => t.status === 'in-progress').length;
                    const overdue = dashboardTasks.filter((t: any) => {
                      if (!t.deadline || t.status === 'done') return false;
                      return new Date(t.deadline) < new Date();
                    }).length;
                    const completionRate = total > 0 ? Math.round((done / total) * 100) : 0;
                    return (
                      <>
                        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-lg bg-blue-50">
                              <FileText className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-gray-900">{total}</p>
                              <p className="text-sm text-gray-500">Total tasks</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-lg bg-emerald-50">
                              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-gray-900">{done}</p>
                              <p className="text-sm text-gray-500">Completed</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-lg bg-amber-50">
                              <Clock className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-gray-900">{inProgress}</p>
                              <p className="text-sm text-gray-500">En cours</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-lg bg-red-50">
                              <AlertCircle className="h-5 w-5 text-red-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-gray-900">{overdue}</p>
                              <p className="text-sm text-gray-500">En retard</p>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Progress & Quick Actions */}
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Progress */}
                  <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4">Progression du projet</h3>
                    {(() => {
                      const total = dashboardTasks.length;
                      const done = dashboardTasks.filter((t: any) => t.status === 'done').length;
                      const rate = total > 0 ? Math.round((done / total) * 100) : 0;
                      return (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600">{rate}% completed</span>
                            <span className="text-sm text-gray-500">{done}/{total} tasks</span>
                          </div>
                          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500"
                              style={{ width: `${rate}%` }}
                            />
                          </div>
                          <div className="flex gap-2 mt-4">
                            {['todo', 'in-progress', 'review', 'done'].map((status) => {
                              const count = dashboardTasks.filter((t: any) => t.status === status).length;
                              const colors: Record<string, string> = {
                                todo: 'bg-gray-200',
                                'in-progress': 'bg-amber-400',
                                review: 'bg-blue-400',
                                done: 'bg-emerald-500'
                              };
                              return (
                                <div key={status} className="flex items-center gap-1.5">
                                  <div className={`w-2 h-2 rounded-full ${colors[status] || 'bg-gray-300'}`} />
                                  <span className="text-xs text-gray-600">{status}: {count}</span>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Quick Actions */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4">Quick access</h3>
                    <div className="space-y-2">
                      {[
                        { icon: Kanban, label: 'Board', tab: 'board', path: `?tab=tasks&view=board` },
                        { icon: FileText, label: 'Tasks', tab: 'tasks', path: `?tab=tasks&view=tasks` },
                        { icon: GanttChart, label: 'Timeline', tab: 'timeline', path: `?tab=tasks&view=timeline` },
                        { icon: MessageSquare, label: 'Messages', tab: 'messages' },
                        { icon: Play, label: 'Planning Poker', tab: 'planning-poker' },
                        { icon: Video, label: 'Meetings', tab: 'meetings' }
                      ].map((item) => (
                        <button
                          key={item.tab}
                          onClick={() => item.path?.startsWith('/') ? navigate(item.path) : navigate(`/projects/${projectId}${item.path || `?tab=${item.tab}`}`)}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
                        >
                          <item.icon className="h-4 w-4 text-gray-600" />
                          <span className="font-medium text-gray-700">{item.label}</span>
                          <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Équipes & Échéances */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Équipes */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4">Teams</h3>
                    {project?.teams && project.teams.length > 0 ? (
                      <div className="space-y-3">
                        {project.teams.slice(0, 4).map((team: any) => (
                          <div key={team._id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <Users className="h-5 w-5 text-indigo-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{team.name}</p>
                              <p className="text-sm text-gray-500">{team.members?.length || 0} membre(s)</p>
                            </div>
                            <button
                              onClick={() => navigate(`/projects/${projectId}/team/${team._id}/collaboration`)}
                              className="text-blue-600 text-sm font-medium hover:underline"
                            >
                              Voir
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No teams</p>
                    )}
                  </div>

                  {/* Upcoming deadlines */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4">Upcoming deadlines</h3>
                    {(() => {
                      const now = new Date();
                      const upcoming = dashboardTasks
                        .filter((t: any) => t.deadline && new Date(t.deadline) >= now && t.status !== 'done')
                        .sort((a: any, b: any) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime())
                        .slice(0, 5);
                      return upcoming.length > 0 ? (
                        <div className="space-y-2">
                          {upcoming.map((task: any) => (
                            <div key={task._id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                              <CalendarIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate text-sm">{task.title}</p>
                                <p className="text-xs text-gray-500">
                                  {new Date(task.deadline).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              </div>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${task.priority === 'urgent' ? 'bg-red-100 text-red-700' : task.priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>
                                {task.priority}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">No upcoming deadlines</p>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}
          </div>
        )}


        {activeTab === 'workflow' && (
          <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Workflow</h2>
            <p className="text-gray-500">Coming soon...</p>
          </div>
        )}

        {activeTab === 'messages' && projectId && (
          <MessagesPage />
        )}

        {activeTab === 'notes' && projectId && (
          <NotesPage />
        )}


        {activeTab === 'meetings' && projectId && project.teams && project.teams.length > 0 && (
          <MeetingCalendar
            projectId={projectId}
            teams={project.teams.map(team => ({
              _id: team._id,
              name: team.name,
              members: team.members || []
            }))}
            currentUser={user || { _id: '', name: '', email: '' }}
          />
        )}

        {activeTab === 'meetings' && projectId && (!project.teams || project.teams.length === 0) && (
          <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 p-6">
            <div className="text-center py-12">
              <Video className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No teams available</h2>
              <p className="text-gray-500 mb-4">
                Create a team to schedule meetings
              </p>
              {isCreator && (
                <button
                  onClick={() => {
                    setShowCreateTeamModal(true);
                    setTeamFormData({ name: '', description: '', memberEmails: [] });
                    setSelectedTeam(null);
                  }}
                  className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create a team
                </button>
              )}
            </div>
          </div>
        )}

        {/* Onglet Équipe - Visible uniquement pour le créateur */}
        {activeTab === 'equipe' && isCreator && (
          <div className="space-y-6">
            {/* Header moderne avec design soft */}
            <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-8 py-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center border border-slate-200 shadow-sm">
                      <Users className="h-8 w-8 text-slate-600" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-semibold text-slate-800 mb-1">Team Management</h2>
                      <p className="text-slate-500 text-sm">
                        Manage your team's members, roles and permissions
                      </p>
                    </div>
              {project.teams && project.teams.length > 0 && (
                      <div className="ml-4 px-4 py-2 bg-white rounded-xl border border-slate-200 shadow-sm">
                        <span className="text-slate-700 font-medium text-lg">
                          {project.teams.length} team{project.teams.length > 1 ? 's' : ''}
                </span>
                      </div>
              )}
            </div>
            {isCreator && (!project.teams || project.teams.length === 0) && (
              <button
                onClick={() => {
                  setShowCreateTeamModal(true);
                  setTeamFormData({ name: '', description: '', memberEmails: [] });
                  setSelectedTeam(null);
                }}
                      className="flex items-center gap-2 px-6 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-all shadow-sm hover:shadow-md font-medium"
              >
                      <Plus className="h-5 w-5" />
                Create a team
              </button>
            )}
            {isCreator && project.teams && project.teams.length > 0 && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-xl border border-slate-200">
                      <Users className="h-5 w-5 text-slate-500" />
                      <span className="text-slate-600 text-sm">1 team maximum</span>
              </div>
            )}
                </div>
              </div>
          </div>

          {project.teams && project.teams.length > 0 ? (
              <div className="space-y-6">
                  {project.teams.map((team) => {
                    const isMember = team.members.some(m => m._id === user?._id);
                    const isTeamCreator = team.creator._id === user?._id;
                    const pendingRequests = team.joinRequests?.filter(r => r.status === 'pending') || [];
                    const hasPendingRequest = pendingRequests.some(r => r.userId === user?._id);
                    const pendingInvites = team.invites ? team.invites.filter(i => i.status === 'pending').length : 0;
                    const isExpanded = expandedTeamId === team._id;

                    return (
                    <div key={team._id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-all duration-200">
                      {/* Carte équipe principale */}
                      <div className="p-6">
                        <div className="flex items-start justify-between">
                          {/* Informations équipe */}
                          <div className="flex items-start gap-4 flex-1">
                            <div className="h-16 w-16 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center font-semibold text-2xl border border-slate-200">
                                {team.name[0].toUpperCase()}
                              </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-xl font-semibold text-slate-800">{team.name}</h3>
                                <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium border border-emerald-200">
                              {team.members.length} membre{team.members.length > 1 ? 's' : ''}
                            </span>
                                {pendingInvites > 0 && (
                                  <span className="px-2.5 py-1 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium border border-amber-200 flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    {pendingInvites} invite{pendingInvites > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                              <p className="text-slate-500 text-sm mb-3">{team.description || 'Aucune description'}</p>
                              <div className="flex items-center gap-2">
                            {isTeamCreator ? (
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                                    <Shield className="h-3 w-3 mr-1" />
                                Creator
                              </span>
                            ) : isMember ? (
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    <User className="h-3 w-3 mr-1" />
                                Membre
                              </span>
                            ) : hasPendingRequest ? (
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                    <Clock className="h-3 w-3 mr-1" />
                                Request sent
                              </span>
                                ) : null}
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                            <div className="flex items-center gap-2">
                              {(isMember || isTeamCreator) && (
                                <button
                                  onClick={() => navigate(`/projects/${projectId}/team/${team._id}/collaboration`)}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-all shadow-sm hover:shadow-md font-medium text-sm"
                                  title="Open collaboration space"
                                >
                                  <Users2 className="h-4 w-4" />
                                  
                                </button>
                              )}
                              {!isTeamCreator && !isMember && !hasPendingRequest && (
                                <button
                                  onClick={() => handleJoinRequest(team._id)}
                                  disabled={submitting}
                                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 disabled:opacity-50 transition-all shadow-sm hover:shadow-md font-medium text-sm"
                                >
                                  <UserPlus className="h-4 w-4" />
                                  Rejoindre
                                </button>
                              )}
                              {isTeamCreator && (
                                <>
                                <button 
                                  onClick={() => handleEditTeamClick(team)} 
                                  className="p-2.5 text-slate-600 hover:bg-slate-100 rounded-lg transition-all" 
                                  title="Modifier"
                                >
                                  <Edit className="h-5 w-5" />
                                  </button>
                                  <button
                                  onClick={() => requestDeleteTeam(team._id)} 
                                  className="p-2.5 text-slate-600 hover:bg-red-50 rounded-lg transition-all" 
                                  title="Supprimer"
                                  >
                                  <Trash2 className="h-5 w-5" />
                                  </button>
                                </>
                              )}
                            </div>
                        </div>
                      </div>

                      {/* Section : Demandes en attente + Gestion membres */}
                      {isTeamCreator && (
                        <div className="border-t border-slate-200 bg-slate-50/50">
                          <div className="p-6 space-y-6">
                            {/* Section Ajouter un membre - Mise en valeur en haut */}
                            <div className="bg-white rounded-xl border-2 border-slate-200 shadow-sm p-6 mb-6">
                              <div className="flex items-center gap-3 mb-4">
                                <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center border border-slate-200">
                                  <UserPlus className="h-6 w-6 text-slate-600" />
                                </div>
                                <div>
                                  <h4 className="text-xl font-semibold text-slate-800 mb-1">Add a Member</h4>
                                  <p className="text-slate-500 text-sm">Invite a new member to join your team</p>
                                </div>
                              </div>
                              <div className="flex gap-3">
                                <input
                                  type="email"
                                  value={newEmailInput}
                                  onChange={(e) => setNewEmailInput(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      if (newEmailInput.trim()) {
                                        handleAddMemberToTeam(team._id, newEmailInput.trim());
                                        setNewEmailInput('');
                                      }
                                    }
                                  }}
                                  placeholder="New member email"
                                  className="flex-1 px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-all text-slate-800 placeholder-slate-400"
                                />
                                <button
                                  onClick={() => {
                                    if (newEmailInput.trim()) {
                                      handleAddMemberToTeam(team._id, newEmailInput.trim());
                                      setNewEmailInput('');
                                    }
                                  }}
                                  className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-all shadow-sm hover:shadow-md font-medium"
                                >
                                  <UserPlus className="h-5 w-5" />
                                </button>
                              </div>
                            </div>

                                {/* Pending join requests */}
                                {pendingRequests.length > 0 && (
                              <div className="bg-amber-50 rounded-xl border border-amber-200 shadow-sm overflow-hidden">
                                        <div className="bg-amber-100 px-6 py-4 border-b border-amber-200">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                          <div className="relative">
                                                <div className="h-10 w-10 rounded-lg bg-amber-200 flex items-center justify-center border border-amber-300">
                                                  <UserPlus className="h-5 w-5 text-amber-700" />
                                            </div>
                                                <div className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                                              <span className="text-xs font-bold text-white">{pendingRequests.length}</span>
                                            </div>
                                          </div>
                                          <div>
                                                <h4 className="text-lg font-semibold text-amber-900 flex items-center gap-2">
                                              Pending join requests
                                            </h4>
                                                <p className="text-sm text-amber-700 mt-0.5">
                                              {pendingRequests.length} request{pendingRequests.length > 1 ? 's' : ''} need{pendingRequests.length > 1 ? '' : 's'} your attention
                                            </p>
                                          </div>
                                        </div>
                                            <div className="px-3 py-1.5 bg-amber-200 rounded-lg border border-amber-300">
                                              <span className="text-sm font-medium text-amber-900">
                                            Action requise
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                        <div className="p-6 space-y-3">
                                      {pendingRequests.map((req, index) => (
                                        <div 
                                          key={req._id} 
                                              className="group bg-white rounded-lg border border-amber-200 shadow-sm hover:shadow-md hover:border-amber-300 transition-all duration-200 overflow-hidden"
                                        >
                                              <div className="p-4">
                                            <div className="flex items-center justify-between">
                                                  <div className="flex items-center gap-3 flex-1">
                                                <div className="relative">
                                                      <div className="h-12 w-12 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center font-semibold text-lg border border-amber-200">
                                                    {req.userName[0].toUpperCase()}
                                                  </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="flex items-center gap-2 mb-1">
                                                        <p className="font-semibold text-slate-800">{req.userName}</p>
                                                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded border border-amber-200">
                                                      En attente
                                                    </span>
                                                  </div>
                                                      <div className="flex items-center gap-2 text-sm text-slate-600">
                                                        <Mail className="h-3.5 w-3.5 text-slate-400" />
                                                    <span className="truncate">{req.userEmail}</span>
                                                  </div>
                                                </div>
                                              </div>
                                                  <div className="flex items-center gap-2 ml-4">
                                                <button
                                                  onClick={() => handleAcceptJoinRequest(team._id, req._id)}
                                                      className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-all shadow-sm hover:shadow-md font-medium text-sm"
                                                >
                                                      <CheckCircle2 className="h-4 w-4" />
                                                  Accepter
                                                </button>
                                                <button
                                                  onClick={() => handleRejectJoinRequest(team._id, req._id)}
                                                      className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-all shadow-sm hover:shadow-md font-medium text-sm"
                                                >
                                                      <XCircle className="h-4 w-4" />
                                                  Refuser
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>

                                        <div className="px-6 py-3 bg-amber-50 border-t border-amber-200">
                                          <div className="flex items-center gap-2 text-sm text-amber-800">
                                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                                        <span className="font-medium">
                                          Accepted requests will automatically add members to the team
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Gestion des membres */}
                                <div className="space-y-4">
                              <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                                <Users className="h-5 w-5 text-slate-500" />
                                <h4 className="text-lg font-semibold text-slate-800">Team Members</h4>
                                <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium border border-slate-200">
                                  {team.members?.length || 0}
                                </span>
                              </div>

                              <div className="grid gap-3">
                                      {team.members?.map((member) => {
                                  const isTeamCreatorMember = team.creator._id === member._id;
                                        return (
                                    <div key={member._id} className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 hover:shadow-sm transition-all">
                                      <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold border border-emerald-200">
                                              {member.name.charAt(0).toUpperCase()}
                                            </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium text-slate-800">{member.name}</span>
                                            {isTeamCreatorMember && (
                                              <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded border border-slate-200">
                                                <Shield className="h-3 w-3 inline mr-1" />
                                                Creator
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-xs text-slate-500 flex items-center gap-1">
                                            <Mail className="h-3 w-3" />
                                            {member.email}
                                          </p>
                                        </div>
                                        {!isTeamCreatorMember && (
                                              <button
                                                onClick={() => handleRemoveMemberFromTeam(team._id, member._id)}
                                            className="p-2 hover:bg-red-50 rounded-lg transition-all text-slate-500 hover:text-red-600"
                                                title="Supprimer membre"
                                              >
                                            <UserMinus className="h-4 w-4" />
                                              </button>
                                        )}
                                      </div>
                                      
                                      {/* Section rôle réduite */}
                                      {projectId && (
                                        <div className="mt-3 pt-3 border-t border-slate-100">
                                          <ProjectMemberRoleManager
                                            projectId={projectId}
                                            userId={member._id}
                                            memberName={member.name}
                                            memberEmail={member.email}
                                            memberGlobalRole={(member as any).globalRole}
                                            isCreator={isCreator}
                                            currentUserId={user?._id}
                                            initialProjectRole={(member as any).projectRole ?? null}
                                            onUpdate={() => {
                                              const fetchProject = async () => {
                                                try {
                                                  const res = await axios.get(`${API_BASE}/projects/${projectId}`, {
                                                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                                  });
                                                  if (res.data.success) {
                                                    setProject(res.data.data);
                                                  }
                                                } catch (err) {
                                                  console.error('Erreur rafraîchissement projet:', err);
                                                }
                                              };
                                              fetchProject();
                                            }}
                                          />
                                        </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                  </div>

                                  {team.invites && team.invites.filter(i => i.status === 'pending').length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                  <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2 text-sm">
                                    <AlertTriangle className="h-4 w-4" />
                                        Invites en Attente
                                      </h4>
                                      <div className="flex flex-wrap gap-2">
                                        {team.invites
                                          .filter(invite => invite.status === 'pending')
                                          .map((invite, index) => (
                                        <div key={index} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-amber-300 rounded-md text-xs shadow-sm">
                                          <Mail className="h-3 w-3 text-amber-600" />
                                          <span className="font-medium text-amber-900">{invite.email}</span>
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  )}
                                    </div>
                                  </div>
                                </div>
                        )}
                    </div>
                    );
                  })}
            </div>
          ) : (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12">
                <div className="text-center">
                  <div className="h-24 w-24 rounded-full bg-slate-100 mx-auto mb-6 flex items-center justify-center border border-slate-200">
                    <Users className="h-12 w-12 text-slate-500" />
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-800 mb-2">No associated team</h3>
                  <p className="text-slate-500 mb-6 max-w-md mx-auto">
                    Create your first team to start collaborating and managing project members
                  </p>
                  {isCreator && (
                <button
                      onClick={() => {
                        setShowCreateTeamModal(true);
                        setTeamFormData({ name: '', description: '', memberEmails: [] });
                        setSelectedTeam(null);
                      }}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-all shadow-sm hover:shadow-md font-medium"
                >
                      <Plus className="h-5 w-5" />
                  Create the first team
                </button>
              )}
            </div>
          </div>
        )}
          </div>
        )}

        {/* Message si l'utilisateur n'est pas créateur et essaie d'accéder à l'onglet Équipe */}
        {activeTab === 'equipe' && !isCreator && (
          <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 p-6">
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Restricted access</h2>
              <p className="text-gray-500">
                Only the project creator can access team management
              </p>
            </div>
          </div>
        )}

        {activeTab === 'votes' && projectId && (
          <div className="space-y-6">
            {/* Collaboration Tabs - masqués quand on accède via Tâches (subtab=tasks) */}
            {!location.search.includes('subtab=tasks') && (
            <div className="bg-white border-b border-gray-200 rounded-t-lg">
              <div className="flex gap-1 px-6">
                <button
                  onClick={() => setCollaborationActiveTab('files-collab')}
                  className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
                    collaborationActiveTab === 'files-collab'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Fichiers collaboratifs
                  </div>
                </button>
              </div>
            </div>
            )}

            {/* Votes Tab Content */}
            {collaborationActiveTab === 'votes' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Votes du projet</h2>
                  <button
                    onClick={() => setShowCreateVoteModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Create a vote
                  </button>
                </div>

                {collaborationVotesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : collaborationVotes.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <VoteIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Aucun vote pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {collaborationVotes.map((vote: any) => {
                      const isActive = vote.status === 'active' && isVoteActive(vote);
                      return (
                        <div
                          key={vote._id}
                          className="bg-white border border-gray-200/60 rounded-xl p-6 hover:shadow-lg transition-all duration-200 shadow-sm"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-semibold text-gray-900">{vote.title}</h3>
                                {isActive ? (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                                    Actif
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                                    Closed
                                  </span>
                                )}
                              </div>
                              {vote.description && (
                                <p className="text-sm text-gray-600 mb-3">{vote.description}</p>
                              )}
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <div className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {vote.creator?.name || 'Utilisateur'}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  Closes: {formatCollaborationDate(vote.endDate)}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {vote.results?.totalVotes || 0} vote{(vote.results?.totalVotes || 0) > 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isActive && (
                                <>
                                  {vote.userHasVoted ? (
                                    <span className="text-sm text-green-600 flex items-center gap-1">
                                      <CheckCircle2 className="h-4 w-4" />
                                      You voted
                                    </span>
                                  ) : vote.canVote?.canVote !== false ? (
                                    <button
                                      onClick={() => {
                                        setSelectedVote(vote);
                                        setSelectedVoteOptions([]);
                                        setShowVoteModal(true);
                                      }}
                                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                                    >
                                      Voter
                                    </button>
                                  ) : (
                                    <span className="text-sm text-gray-500">{vote.canVote?.reason || 'Vous ne pouvez pas voter'}</span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>

                          {vote.type !== 'time_estimation' && (
                            <div className="mb-4">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Options :</h4>
                              <div className="space-y-2">
                                {vote.options?.map((option: any, index: number) => {
                                  const result = vote.results?.optionResults?.find((r: any) => r.optionIndex === index);
                                  const percentage = result?.percentage || 0;
                                  const votesCount = result?.votes || 0;
                                  return (
                                    <div key={index} className="space-y-1">
                                      <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-800 flex-1">{option.text}</span>
                                        {canShowResults(vote) && (
                                          <div className="flex items-center gap-2 text-xs text-gray-600">
                                            <BarChart3 className="h-3 w-3" />
                                            <span>{votesCount} vote{votesCount > 1 ? 's' : ''}</span>
                                            <span>({percentage.toFixed(1)}%)</span>
                                          </div>
                                        )}
                                      </div>
                                      {canShowResults(vote) && (
                                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                          <div
                                            className="h-full bg-blue-500"
                                            style={{ width: `${percentage}%` }}
                                          ></div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {canShowResults(vote) && hasValidResults(vote) && (
                            <div className={`mt-4 pt-4 border-t border-gray-200 p-4 rounded-lg ${
                              vote.results.winner.isTie
                                ? 'bg-orange-50 border-orange-200'
                                : 'bg-green-50 border-green-200'
                            }`}>
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${
                                  vote.results.winner.isTie ? 'bg-orange-500' : 'bg-green-500'
                                }`}>
                                  <TrendingUp className="h-5 w-5 text-white" />
                                </div>
                                <div className="flex-1">
                                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Result</span>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {vote.results.winner.isTie ? (
                                      <>
                                        <span className="text-lg font-bold text-orange-700">Tie detected</span>
                                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">Aucun gagnant</span>
                                      </>
                                    ) : (
                                      <>
                                        <span className="text-lg font-bold text-gray-900">{vote.results.winner.optionText}</span>
                                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Gagnant</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Tasks Tab Content */}
            {/* Files Collaboration Tab Content */}
            {collaborationActiveTab === 'files-collab' && projectId && (
              <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 p-6">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-gray-900">Documents Collaboratifs</h2>
                    <button
                      onClick={() => navigate(`/projects/${projectId}/file-collaboration`)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm hover:shadow-md font-medium"
                    >
                      <FileText className="h-4 w-4" />
                      Nouveau document
                    </button>
                  </div>
                  
                  {/* Filtres de type de fichier */}
                  <div className="flex items-center gap-2 mb-6">
                    <button className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg border border-indigo-200 font-medium text-sm">
                      Tous
                    </button>
                    <button className="px-4 py-2 bg-white text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 font-medium text-sm flex items-center gap-2">
                      <Type className="h-4 w-4" />
                      Word
                    </button>
                    <button className="px-4 py-2 bg-white text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 font-medium text-sm flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" />
                      PDF
                    </button>
                  </div>
                </div>

                {/* Carte de document principal */}
                <div
                  onClick={() => navigate(`/projects/${projectId}/file-collaboration`)}
                  className="group relative bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border-2 border-indigo-200 p-6 cursor-pointer hover:border-indigo-300 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-white rounded-lg border border-indigo-200 shadow-sm">
                        <FileText className="h-8 w-8 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                          Document Collaboratif
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Edit in real time with your team
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                        En ligne
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-indigo-200">
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>Last modified moments ago</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors">
                        <Star className="h-4 w-4" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors">
                        <Share2 className="h-4 w-4" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors">
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Message d'aide */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>💡 Tip:</strong> Click "New document" to create a collaborative file. 
                    You can import Word (.doc, .docx) or PDF files to edit collaboratively.
                  </p>
                </div>
              </div>
            )}

            {/* Create Vote Modal */}
            {showCreateVoteModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Create a vote</h3>
                    <button
                      onClick={() => setShowCreateVoteModal(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <form onSubmit={handleCreateVote} className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                      <input
                        type="text"
                        value={voteFormData.title}
                        onChange={e => setVoteFormData({ ...voteFormData, title: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={voteFormData.description}
                        onChange={e => setVoteFormData({ ...voteFormData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type de vote</label>
                      <select
                        value={voteFormData.type}
                        onChange={e => setVoteFormData({ ...voteFormData, type: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="simple">Vote simple</option>
                        <option value="multiple">Choix multiples</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Options *</label>
                      {voteFormData.options.map((option, index) => (
                        <div key={index} className="flex items-center gap-2 mb-2">
                          <input
                            type="text"
                            value={option.text}
                            onChange={e => {
                              const newOptions = [...voteFormData.options];
                              newOptions[index].text = e.target.value;
                              setVoteFormData({ ...voteFormData, options: newOptions });
                            }}
                            placeholder={`Option ${index + 1}`}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                            required
                          />
                          {voteFormData.options.length > 2 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newOptions = voteFormData.options.filter((_, i) => i !== index);
                                setVoteFormData({ ...voteFormData, options: newOptions });
                              }}
                              className="p-2 text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setVoteFormData({
                            ...voteFormData,
                            options: [...voteFormData.options, { text: '', order: voteFormData.options.length }]
                          });
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Plus className="h-4 w-4" />
                        Ajouter une option
                      </button>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin *</label>
                      <input
                        type="datetime-local"
                        value={voteFormData.endDate}
                        onChange={e => setVoteFormData({ ...voteFormData, endDate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Results visibility</label>
                      <select
                        value={voteFormData.resultsVisibility}
                        onChange={e => setVoteFormData({ ...voteFormData, resultsVisibility: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="hidden">Hidden until closed</option>
                        <option value="realtime">Real-time</option>
                        <option value="after_close">After closing</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="allowModification"
                        checked={voteFormData.allowVoteModification}
                        onChange={e => setVoteFormData({ ...voteFormData, allowVoteModification: e.target.checked })}
                      />
                      <label htmlFor="allowModification" className="text-sm text-gray-700">Permettre la modification du vote</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="isAnonymous"
                        checked={voteFormData.isAnonymous}
                        onChange={e => setVoteFormData({ ...voteFormData, isAnonymous: e.target.checked })}
                      />
                      <label htmlFor="isAnonymous" className="text-sm text-gray-700">Vote anonyme</label>
                    </div>
                    <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                      <button
                        type="submit"
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Create vote
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCreateVoteModal(false)}
                        className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Vote Modal */}
            {showVoteModal && selectedVote && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-lg w-full">
                  <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <h3 className="text-xl font-semibold">{selectedVote.title}</h3>
                    <button
                      onClick={() => {
                        setShowVoteModal(false);
                        setSelectedVote(null);
                        setSelectedVoteOptions([]);
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="p-6 space-y-4">
                    {selectedVote.description && (
                      <p className="text-sm text-gray-600">{selectedVote.description}</p>
                    )}
                    <div className="space-y-2">
                      {selectedVote.options?.map((option: any, index: number) => (
                        <label
                          key={index}
                          className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                            selectedVoteOptions.includes(index)
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type={selectedVote.type === 'simple' ? 'radio' : 'checkbox'}
                            name="voteOption"
                            checked={selectedVoteOptions.includes(index)}
                            onChange={(e) => {
                              if (selectedVote.type === 'simple') {
                                setSelectedVoteOptions([index]);
                              } else {
                                if (e.target.checked) {
                                  setSelectedVoteOptions([...selectedVoteOptions, index]);
                                } else {
                                  setSelectedVoteOptions(selectedVoteOptions.filter(i => i !== index));
                                }
                              }
                            }}
                            className="h-4 w-4 text-blue-600"
                          />
                          <span className="flex-1">{option.text}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                      <button
                        onClick={handleSubmitVote}
                        disabled={selectedVoteOptions.length === 0}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Soumettre le vote
                      </button>
                      <button
                        onClick={() => {
                          setShowVoteModal(false);
                          setSelectedVote(null);
                          setSelectedVoteOptions([]);
                        }}
                        className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Create Collaboration Task Modal */}
            {showCreateCollaborationTaskModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Create a task</h3>
                    <button
                      onClick={() => setShowCreateCollaborationTaskModal(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <form onSubmit={handleCreateCollaborationTask} className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                      <input
                        type="text"
                        value={collaborationTaskFormData.title}
                        onChange={e => setCollaborationTaskFormData({ ...collaborationTaskFormData, title: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={collaborationTaskFormData.description}
                        onChange={e => setCollaborationTaskFormData({ ...collaborationTaskFormData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows={4}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                        <select
                          value={collaborationTaskFormData.priority}
                          onChange={e => setCollaborationTaskFormData({ ...collaborationTaskFormData, priority: e.target.value as any })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="low">Basse</option>
                          <option value="medium">Moyenne</option>
                          <option value="high">Haute</option>
                          <option value="urgent">Urgente</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date limite</label>
                        <input
                          type="datetime-local"
                          value={collaborationTaskFormData.deadline}
                          onChange={e => setCollaborationTaskFormData({ ...collaborationTaskFormData, deadline: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                    {project?.teams && project.teams.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
                        <select
                          value={collaborationTaskFormData.team}
                          onChange={e => setCollaborationTaskFormData({ ...collaborationTaskFormData, team: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="">No teams</option>
                          {project.teams.map((team: any) => (
                            <option key={team._id} value={team._id}>{team.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                      <button
                        type="submit"
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Create task
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCreateCollaborationTaskModal(false)}
                        className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

          </div>
        )}

        {/* Planning Poker Tab */}
        {/* Planning Poker Tab - Also accessible from Collaboration */}
        {(activeTab === 'planning-poker' || (activeTab === 'votes' && collaborationActiveTab === 'planning-poker')) && projectId && (
          <PlanningPokerContent projectId={projectId} project={project} user={user} />
        )}
      </div>

      {/* Edit Project Modal */}
      {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h3 className="text-2xl font-bold text-gray-900">Modifier le projet</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              <form onSubmit={handleUpdateProject} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre *
                  </label>
                  <input
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Titre du projet"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Description du projet"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start date *
                    </label>
                    <input
                      type="datetime-local"
                      name="start"
                      required
                      value={formData.start}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date de fin *
                    </label>
                    <input
                      type="datetime-local"
                      name="end"
                      required
                      value={formData.end}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="meeting">Meeting</option>
                    <option value="workshop">Travail</option>
                    <option value="deadline">Deadline</option>
                    <option value="social">Personnel</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    name="isPublic"
                    type="checkbox"
                    checked={formData.isPublic}
                    onChange={handleFormChange}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700">
                    Projet public
                  </label>
                </div>
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-5 w-5 mr-2" />
                        Enregistrer
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
       
        {/* Create/Edit Team Modal */}
        {(showCreateTeamModal || showEditTeamModal) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
              <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-900">
                  {showEditTeamModal ? 'Edit team' : 'Create a team'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateTeamModal(false);
                    setShowEditTeamModal(false);
                    setTeamFormData({ name: '', description: '', memberEmails: [] });
                    setNewEmailInput('');
                    setError(null);
                    setSelectedTeam(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              <form onSubmit={showEditTeamModal ? handleEditTeam : handleCreateTeam} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team name *
                  </label>
                  <input
                    name="name"
                    required
                    value={teamFormData.name}
                    onChange={handleTeamFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Team name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={teamFormData.description}
                    onChange={handleTeamFormChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Team description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nouveaux Membres (Emails pour invitations) *
                  </label>
                  
                  {/* Input pour ajouter un email */}
                  <div className="flex gap-2 mb-4">
                    <div className="flex-1 relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        value={newEmailInput}
                        onChange={(e) => setNewEmailInput(e.target.value)}
                        onKeyPress={handleEmailInputKeyPress}
                        placeholder="Entrez l'email du membre"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleAddEmail}
                      disabled={!newEmailInput.trim()}
                      className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Ajouter
                    </button>
                  </div>

                  {/* Liste des emails ajoutés */}
                  {teamFormData.memberEmails.length > 0 ? (
                    <div className="space-y-2 mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Emails to invite ({teamFormData.memberEmails.length})
                      </p>
                      <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-lg border border-gray-200 min-h-[80px]">
                        {teamFormData.memberEmails.map((email, index) => (
                          <div
                            key={index}
                            className="group flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:border-purple-400 transition-all"
                          >
                            <Mail className="h-4 w-4 text-purple-500 flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-700">{email}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveEmail(email)}
                              className="ml-1 p-1 hover:bg-red-100 rounded-full transition-colors"
                              title="Supprimer"
                            >
                              <XCircle className="h-4 w-4 text-gray-400 hover:text-red-600" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">
                        {showEditTeamModal 
                          ? 'These new emails will receive invitations after saving.'
                          : 'Invitations will be sent automatically to these addresses after the team is created.'
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="p-6 border-2 border-dashed border-gray-300 rounded-lg text-center bg-gray-50">
                      <Mail className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">
                        No new members added. Use the field above to add invitations.
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateTeamModal(false);
                      setShowEditTeamModal(false);
                      setTeamFormData({ name: '', description: '', memberEmails: [] });
                      setNewEmailInput('');
                      setError(null);
                      setSelectedTeam(null);
                    }}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || teamFormData.memberEmails.length === 0}
                    className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        {showEditTeamModal ? 'Saving...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        <Plus className="h-5 w-5 mr-2" />
                        {showEditTeamModal ? 'Save and Send New Invites' : 'Create and Send Invites'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-6">
                <div className="flex items-start mb-6">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Confirmation</h3>
                    <p className="text-gray-700">{confirmMessage}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={submitting}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Traitement...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-5 w-5 mr-2" />
                        Confirmer
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Task Modal */}
        {showCreateTaskModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">Create Task</h3>
                <button
                  onClick={() => {
                    setShowCreateTaskModal(false);
                    setTaskFormData({
                      title: '',
                      description: '',
                      status: 'todo',
                      priority: 'medium',
                      deadline: '',
                      assignee: '',
                      team: ''
                    });
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
      </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!taskFormData.team || !projectId) {
                    alert('Please select a team');
                    return;
                  }
                  try {
                    await axios.post(
                      `${API_BASE}/tasks/team/${taskFormData.team}`,
                      {
                        title: taskFormData.title,
                        description: taskFormData.description,
                        status: taskFormData.status,
                        priority: taskFormData.priority,
                        deadline: taskFormData.deadline || null,
                        assignee: taskFormData.assignee || null
                      },
                      {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                      }
                    );
                    setShowCreateTaskModal(false);
                    setTaskFormData({
                      title: '',
                      description: '',
                      status: 'todo',
                      priority: 'medium',
                      deadline: '',
                      assignee: '',
                      team: ''
                    });
                    // Trigger refresh in TaskListView
                    window.dispatchEvent(new CustomEvent('refreshTaskList'));
                  } catch (err: any) {
                    console.error('Erreur création tâche:', err);
                    alert(err.response?.data?.error || 'Error creating item');
                  }
                }}
                className="p-6 space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    required
                    value={taskFormData.title}
                    onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Task title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={taskFormData.description}
                    onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Task description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Team *</label>
                    <select
                      required
                      value={taskFormData.team}
                      onChange={(e) => setTaskFormData({ ...taskFormData, team: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a team</option>
                      {project?.teams?.map((team) => (
                        <option key={team._id} value={team._id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={taskFormData.status}
                      onChange={(e) => setTaskFormData({ ...taskFormData, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="todo">To do</option>
                      <option value="in-progress">Doing</option>
                      <option value="review">Review</option>
                      <option value="done">Done</option>
                      <option value="blocked">Blocked</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select
                      value={taskFormData.priority}
                      onChange={(e) => setTaskFormData({ ...taskFormData, priority: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                    <input
                      type="date"
                      value={taskFormData.deadline}
                      onChange={(e) => setTaskFormData({ ...taskFormData, deadline: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assignee</label>
                  <select
                    value={taskFormData.assignee}
                    onChange={(e) => setTaskFormData({ ...taskFormData, assignee: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Unassigned</option>
                    {project?.teams
                      ?.find((t) => t._id === taskFormData.team)
                      ?.members?.map((member: any) => (
                        <option key={member._id} value={member._id}>
                          {member.name || member.email}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateTaskModal(false);
                      setTaskFormData({
                        title: '',
                        description: '',
                        status: 'todo',
                        priority: 'medium',
                        deadline: '',
                        assignee: '',
                        team: ''
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    Create Task
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Task Detail Modal with Tabs */}
        {showTaskDetailModal && selectedTask && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{selectedTask.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedTask.assignee ? `Assigned to: ${typeof selectedTask.assignee === 'object' ? selectedTask.assignee.name : selectedTask.assignee}` : 'Unassigned'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowTaskDetailModal(false);
                    setSelectedTask(null);
                    setTaskDetailTab('details');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b px-6 flex gap-4">
                <button
                  onClick={() => setTaskDetailTab('details')}
                  className={`py-3 px-2 border-b-2 transition-colors ${
                    taskDetailTab === 'details'
                      ? 'border-blue-500 text-blue-600 font-medium'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Details
                </button>
                <button
                  onClick={() => setTaskDetailTab('files')}
                  className={`py-3 px-2 border-b-2 transition-colors ${
                    taskDetailTab === 'files'
                      ? 'border-blue-500 text-blue-600 font-medium'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Fichiers ({taskFiles[selectedTask._id]?.length || 0})
                </button>
                {(() => {
                  // Afficher l'onglet Validation si :
                  // - Le créateur peut toujours voir cet onglet
                  // - OU il y a une soumission (pour que tous les membres puissent voter)
                  // - OU l'utilisateur est membre d'une équipe du projet (pour voir les votes)
                  const taskAssigneeId = selectedTask.assignee 
                    ? (typeof selectedTask.assignee === 'object' ? selectedTask.assignee._id : selectedTask.assignee)
                    : null;
                  const isCreatorTask = isCreator && taskAssigneeId === user?._id;
                  
                  // Vérifier si l'utilisateur est membre d'une équipe du projet
                  const isTeamMember = project?.teams?.some((team: any) => 
                    team.members?.some((member: any) => {
                      const memberId = typeof member === 'object' ? member._id : member;
                      return memberId === user?._id;
                    })
                  ) || false;
                  
                  const showValidationTab = isCreator || taskSubmissions[selectedTask._id] || isCreatorTask || isTeamMember;
                  
                  return showValidationTab ? (
                    <button
                      onClick={async () => {
                        setTaskDetailTab('validation');
                        // TOUJOURS récupérer la soumission et les votes pour tous les utilisateurs
                        if (selectedTask) {
                          // Toujours récupérer la soumission (même si elle existe déjà, pour être sûr)
                          await fetchTaskSubmission(selectedTask._id);
                          // Attendre un peu pour que le state soit mis à jour
                          setTimeout(async () => {
                            const submission = taskSubmissions[selectedTask._id];
                            if (submission && (submission.status === 'submitted' || submission.status === 'under-review')) {
                              await fetchTaskValidationVotes(selectedTask._id);
                            } else if (selectedTask.status === 'submitted' || selectedTask.status === 'under-review') {
                              await fetchTaskValidationVotes(selectedTask._id);
                            }
                          }, 200);
                        }
                      }}
                      className={`py-3 px-2 border-b-2 transition-colors ${
                        taskDetailTab === 'validation'
                          ? 'border-blue-500 text-blue-600 font-medium'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Validation {taskSubmissions[selectedTask._id] && !isCreator && '(En attente)'}
                    </button>
                  ) : null;
                })()}
                {taskValidations[selectedTask._id]?.length > 0 && (
                  <button
                    onClick={() => setTaskDetailTab('feedback')}
                    className={`py-3 px-2 border-b-2 transition-colors ${
                      taskDetailTab === 'feedback'
                        ? 'border-blue-500 text-blue-600 font-medium'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Feedback ({taskValidations[selectedTask._id]?.length || 0})
                  </button>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {taskDetailTab === 'details' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <p className="text-gray-900">{selectedTask.description || 'Aucune description'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm ${getStatusColor(selectedTask.status)}`}>
                          {selectedTask.status}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm ${
                          selectedTask.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                          selectedTask.priority === 'high' ? 'bg-purple-100 text-purple-700' :
                          selectedTask.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {selectedTask.priority}
                        </span>
                      </div>
                      {selectedTask.deadline && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Deadline</label>
                          <p className="text-gray-900">{new Date(selectedTask.deadline).toLocaleDateString('en-US')}</p>
                        </div>
                      )}
                    </div>
                    {selectedTask.status === 'in-progress' && !isCreator && (
                      <div className="pt-4 border-t">
                        <button
                          onClick={() => handleSubmitTask(selectedTask._id)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Soumettre pour validation
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {taskDetailTab === 'files' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="text-lg font-semibold">Fichiers de travail</h4>
                      {(selectedTask.status === 'in-progress' || selectedTask.status === 'todo') && (
                        <label className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
                          <Upload className="h-4 w-4 inline mr-2" />
                          Upload fichiers
                          <input
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => handleUploadFiles(selectedTask._id, e.target.files)}
                          />
                        </label>
                      )}
                    </div>
                    {uploadingFiles[selectedTask._id] ? (
                      <div className="text-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
                        <p className="mt-2 text-gray-500">Upload en cours...</p>
                      </div>
                    ) : taskFiles[selectedTask._id]?.length > 0 ? (
                      <div className="space-y-2">
                        {taskFiles[selectedTask._id].map((file: any) => (
                          <div key={file._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileIcon className="h-5 w-5 text-gray-400" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                <p className="text-xs text-gray-500">
                                  {(file.size / 1024).toFixed(2)} KB • {new Date(file.uploadedAt).toLocaleDateString('en-US')}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleDownloadFile(selectedTask._id, file._id, file.name)}
                                className="p-2 hover:bg-gray-200 rounded-lg"
                                title="Download"
                              >
                                <Download className="h-4 w-4 text-gray-600" />
                              </button>
                              {!isCreator && (
                                <button
                                  onClick={() => handleDeleteFile(selectedTask._id, file._id)}
                                  className="p-2 hover:bg-red-100 rounded-lg"
                                  title="Supprimer"
                                >
                                  <X className="h-4 w-4 text-red-600" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <FileIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No files uploaded</p>
                      </div>
                    )}
                  </div>
                )}

                {taskDetailTab === 'validation' && (
                  <div className="space-y-4">
                    {isCreator ? (() => {
                      // Vérifier si la tâche est assignée au créateur
                      const taskAssigneeId = selectedTask.assignee 
                        ? (typeof selectedTask.assignee === 'object' ? selectedTask.assignee._id : selectedTask.assignee)
                        : null;
                      const isCreatorTask = taskAssigneeId === user?._id;
                      const hasSubmission = taskSubmissions[selectedTask._id];
                      
                      // Afficher le formulaire si : il y a une soumission OU c'est la tâche du créateur
                      if (hasSubmission || isCreatorTask) {
                        return (
                          <div>
                            <h4 className="text-lg font-semibold">Validate task</h4>
                            <div className="space-y-4">
                              {hasSubmission ? (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                  <p className="text-sm text-blue-900">
                                    <strong>Soumission du:</strong> {new Date(taskSubmissions[selectedTask._id].submittedAt).toLocaleDateString('en-US', {
                                      day: 'numeric',
                                      month: 'long',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                  {taskSubmissions[selectedTask._id].comment && (
                                    <p className="text-sm text-blue-800 mt-2">
                                      <strong>Commentaire:</strong> {taskSubmissions[selectedTask._id].comment}
                                    </p>
                                  )}
                                </div>
                              ) : isCreatorTask ? (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                  <p className="text-sm text-green-900 font-medium">
                                    ✓ Task assigned to you
                                  </p>
                                  <p className="text-xs text-green-800 mt-1">
                                    As the project creator, you can validate this task directly without prior submission.
                                  </p>
                                </div>
                              ) : null}

                              {/* Votes de validation de l'équipe */}
                              {hasSubmission && taskValidationVotes[selectedTask._id] && (
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                  <h5 className="text-sm font-semibold text-purple-900 mb-3">Team votes</h5>
                                  <div className="grid grid-cols-2 gap-4 mb-3">
                                    <div className="bg-green-100 rounded-lg p-3">
                                      <div className="text-2xl font-bold text-green-700">
                                        {taskValidationVotes[selectedTask._id].validVotes || 0}
                                      </div>
                                      <div className="text-xs text-green-600">Votes Valides</div>
                                    </div>
                                    <div className="bg-red-100 rounded-lg p-3">
                                      <div className="text-2xl font-bold text-red-700">
                                        {taskValidationVotes[selectedTask._id].invalidVotes || 0}
                                      </div>
                                      <div className="text-xs text-red-600">Votes Non Valides</div>
                                    </div>
                                  </div>
                                  <div className="text-xs text-purple-700">
                                    <strong>Total:</strong> {taskValidationVotes[selectedTask._id].totalVotes || 0} / {taskValidationVotes[selectedTask._id].teamSize || 0} membres
                                    {taskValidationVotes[selectedTask._id].canBeValidated ? (
                                      <span className="ml-2 text-green-700 font-semibold">✓ Ready to validate</span>
                                    ) : (
                                      <span className="ml-2 text-yellow-700 font-semibold">⚠️ Unfavorable votes (you can still validate)</span>
                                    )}
                                  </div>
                                  {taskValidationVotes[selectedTask._id].validVotes <= taskValidationVotes[selectedTask._id].invalidVotes && (
                                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                                      ℹ️ As the creator, you can validate directly even if votes are unfavorable.
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Section de vote pour le créateur */}
                              {hasSubmission && (() => {
                                // Vérifier si le créateur est celui qui a soumis la tâche
                                const submission = taskSubmissions[selectedTask._id];
                                const submittedBy = submission.submittedBy 
                                  ? (typeof submission.submittedBy === 'object' ? submission.submittedBy._id : submission.submittedBy)
                                  : null;
                                const isCreatorSubmitter = submittedBy === user?._id;
                                
                                return (
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h5 className="text-sm font-semibold text-blue-900 mb-3">Votre vote</h5>
                                    
                                    {isCreatorSubmitter ? (
                                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                        <p className="text-sm text-yellow-800">
                                          ⚠️ You cannot vote on your own submission. You can however validate the task directly below.
                                        </p>
                                      </div>
                                    ) : (
                                      <>
                                        <p className="text-xs text-blue-700 mb-3">
                                          As the project creator, you can vote like other members.
                                        </p>
                                        
                                        {/* Afficher les résultats des votes */}
                                        {taskValidationVotes[selectedTask._id] && (
                                          <div className="mb-4 p-3 bg-white rounded-lg border border-blue-200">
                                            <div className="grid grid-cols-2 gap-3 mb-2">
                                              <div className="text-center">
                                                <div className="text-xl font-bold text-green-600">
                                                  {taskValidationVotes[selectedTask._id].validVotes || 0}
                                                </div>
                                                <div className="text-xs text-gray-600">Valides</div>
                                              </div>
                                              <div className="text-center">
                                                <div className="text-xl font-bold text-red-600">
                                                  {taskValidationVotes[selectedTask._id].invalidVotes || 0}
                                                </div>
                                                <div className="text-xs text-gray-600">Non Valides</div>
                                              </div>
                                            </div>
                                            <div className="text-center text-xs text-gray-600">
                                              {taskValidationVotes[selectedTask._id].totalVotes || 0} / {taskValidationVotes[selectedTask._id].teamSize || 0} members voted
                                            </div>
                                          </div>
                                        )}

                                        {/* Boutons de vote */}
                                        <div className="flex gap-3">
                                          <button
                                            onClick={() => handleVoteOnValidation(selectedTask._id, 'valid')}
                                            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                                              userValidationVote[selectedTask._id]?.vote === 'valid'
                                                ? 'bg-green-600 text-white'
                                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                                            }`}
                                          >
                                            <CheckCircle className="h-4 w-4 inline mr-1" /> Valide
                                          </button>
                                          <button
                                            onClick={() => handleVoteOnValidation(selectedTask._id, 'invalid')}
                                            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                                              userValidationVote[selectedTask._id]?.vote === 'invalid'
                                                ? 'bg-red-600 text-white'
                                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                                            }`}
                                          >
                                            <X className="h-4 w-4 inline mr-1" /> Non Valide
                                          </button>
                                        </div>
                                        {userValidationVote[selectedTask._id] && (
                                          <p className="text-xs text-blue-700 mt-2">
                                            You voted: <strong>{userValidationVote[selectedTask._id].vote === 'valid' ? 'Valid' : 'Invalid'}</strong>
                                            {userValidationVote[selectedTask._id].comment && (
                                              <span className="ml-2">({userValidationVote[selectedTask._id].comment})</span>
                                            )}
                                          </p>
                                        )}
                                      </>
                                    )}
                                  </div>
                                );
                              })()}

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Statut de validation *</label>
                          <select
                            value={validationFormData.status}
                            onChange={(e) => setValidationFormData({ ...validationFormData, status: e.target.value as any })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="approved">Approved</option>
                            <option value="revision-requested">Revision requested</option>
                            <option value="rejected">Rejected</option>
                          </select>
                          {validationFormData.status === 'approved' && hasSubmission && taskValidationVotes[selectedTask._id] && !taskValidationVotes[selectedTask._id].canBeValidated && (
                            <p className="mt-1 text-xs text-yellow-600">
                              ℹ️ Team votes are unfavorable, but as the creator you can validate anyway.
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Feedback *</label>
                          <textarea
                            value={validationFormData.feedback}
                            onChange={(e) => setValidationFormData({ ...validationFormData, feedback: e.target.value })}
                            rows={5}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="Donnez votre feedback sur le travail soumis..."
                            required
                          />
                        </div>

                        {validationFormData.status === 'revision-requested' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Points to fix</label>
                            <div className="space-y-2">
                              {validationFormData.pointsToFix.map((point, idx) => (
                                <div key={idx} className="flex gap-2">
                                  <input
                                    type="text"
                                    value={point}
                                    onChange={(e) => {
                                      const newPoints = [...validationFormData.pointsToFix];
                                      newPoints[idx] = e.target.value;
                                      setValidationFormData({ ...validationFormData, pointsToFix: newPoints });
                                    }}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                    placeholder="Point to fix"
                                  />
                                  <button
                                    onClick={() => {
                                      const newPoints = validationFormData.pointsToFix.filter((_, i) => i !== idx);
                                      setValidationFormData({ ...validationFormData, pointsToFix: newPoints });
                                    }}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => setValidationFormData({ ...validationFormData, pointsToFix: [...validationFormData.pointsToFix, ''] })}
                                className="text-sm text-blue-600 hover:text-blue-700"
                              >
                                + Ajouter un point
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="flex gap-3 pt-4">
                          <button
                            onClick={() => handleValidateTask(selectedTask._id)}
                            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <CheckCircle className="h-4 w-4 inline mr-2" />
                            Valider
                          </button>
                          <button
                            onClick={() => {
                              setValidationFormData({
                                status: 'approved',
                                feedback: '',
                                pointsToFix: []
                              });
                            }}
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                          >
                            Reset
                          </button>
                        </div>
                            </div>
                          </div>
                        );
                      } else {
                        // Pas de soumission et ce n'est pas la tâche du créateur
                        return (
                          <div className="text-center py-8 text-gray-500">
                            <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                            <p className="text-lg font-medium mb-2">This task has not been submitted for validation yet</p>
                            <p className="text-sm mb-4">
                              {selectedTask.assignee 
                                ? `The assigned member (${typeof selectedTask.assignee === 'object' ? selectedTask.assignee.name : selectedTask.assignee}) must submit the task before you can validate it.`
                                : 'No member is assigned to this task. Assign a member and wait for them to submit it.'}
                            </p>
                            {selectedTask.status === 'in-progress' && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 max-w-md mx-auto">
                                <p className="text-sm text-blue-900">
                                  <strong>💡 Tip:</strong> The task is in progress. The assigned member can submit it from the "Details" or "Validation" tab.
                                </p>
                              </div>
                            )}
                            {selectedTask.status === 'todo' && (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4 max-w-md mx-auto">
                                <p className="text-sm text-yellow-900">
                                  <strong>⚠️ Note :</strong> The task is still "To do". It must be "In progress" to be submitted.
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      }
                    })() : (
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold">Statut de validation</h4>
                        {/* Toujours afficher la section de vote si la tâche est soumise, même si la soumission n'est pas encore dans le state */}
                        {(taskSubmissions[selectedTask._id] || selectedTask.status === 'submitted' || selectedTask.status === 'under-review') ? (
                          <>
                            {taskSubmissions[selectedTask._id] && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-900">
                                  <strong>Soumission du:</strong> {new Date(taskSubmissions[selectedTask._id].submittedAt).toLocaleDateString('en-US', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            <p className="text-sm text-blue-800 mt-2">
                              <strong>Statut:</strong> {taskSubmissions[selectedTask._id].status === 'submitted' ? 'En attente de validation' :
                                                         taskSubmissions[selectedTask._id].status === 'approved' ? 'Approved' :
                                                         taskSubmissions[selectedTask._id].status === 'revision-requested' ? 'Revision requested' :
                                                         'Rejected'}
                            </p>
                            {taskSubmissions[selectedTask._id].comment && (
                              <p className="text-sm text-blue-800 mt-2">
                                    <strong>Commentaire:</strong> {taskSubmissions[selectedTask._id].comment}
                              </p>
                            )}
                            {taskValidations[selectedTask._id]?.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-blue-300">
                                <p className="text-sm font-medium text-blue-900 mb-2">Feedback du validateur:</p>
                                <p className="text-sm text-blue-800">{taskValidations[selectedTask._id][0].feedback}</p>
                                {taskValidations[selectedTask._id][0].pointsToFix?.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-sm font-medium text-blue-900 mb-1">Points to fix:</p>
                                    <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
                                      {taskValidations[selectedTask._id][0].pointsToFix.map((point: string, idx: number) => (
                                        <li key={idx}>{point}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                              </div>
                            )}

                            {/* Section de vote pour les membres de l'équipe - TOUJOURS affichée si soumission */}
                            {(taskSubmissions[selectedTask._id]?.status === 'submitted' || selectedTask.status === 'submitted' || selectedTask.status === 'under-review') && (() => {
                              // Vérifier si l'utilisateur actuel est celui qui a soumis la tâche
                              const submission = taskSubmissions[selectedTask._id];
                              const submittedBy = submission?.submittedBy 
                                ? (typeof submission.submittedBy === 'object' ? submission.submittedBy._id : submission.submittedBy)
                                : null;
                              // Si pas de soumission dans le state mais la tâche est soumise, on ne peut pas vérifier qui a soumis
                              // Dans ce cas, on affiche les boutons de vote (le backend vérifiera)
                              const isSubmitter = submission ? (submittedBy === user?._id) : false;
                              
                              // Le créateur du projet peut voter (en plus de pouvoir valider)
                              
                              return (
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                  <h5 className="text-sm font-semibold text-purple-900 mb-3">Voter sur la validation</h5>
                                  
                                  {isSubmitter ? (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                      <p className="text-sm text-yellow-800">
                                        ⚠️ You cannot vote on your own submission. Wait for other team members to vote.
                                      </p>
                          </div>
                        ) : (
                                    <>
                                      <p className="text-xs text-purple-700 mb-3">
                                        {isCreator 
                                          ? "As the project creator, you can vote on this submitted task. You can also validate it directly in the validation section below."
                                          : "As a team member, you can vote on this submitted task."}
                                      </p>
                                      
                                      {/* Afficher les résultats des votes */}
                                      {taskValidationVotes[selectedTask._id] && (
                                        <div className="mb-4 p-3 bg-white rounded-lg border border-purple-200">
                                          <div className="grid grid-cols-2 gap-3 mb-2">
                                            <div className="text-center">
                                              <div className="text-xl font-bold text-green-600">
                                                {taskValidationVotes[selectedTask._id].validVotes || 0}
                                              </div>
                                              <div className="text-xs text-gray-600">Valides</div>
                                            </div>
                                            <div className="text-center">
                                              <div className="text-xl font-bold text-red-600">
                                                {taskValidationVotes[selectedTask._id].invalidVotes || 0}
                                              </div>
                                              <div className="text-xs text-gray-600">Non Valides</div>
                                            </div>
                                          </div>
                                          <div className="text-center text-xs text-gray-600">
                                            {taskValidationVotes[selectedTask._id].totalVotes || 0} / {taskValidationVotes[selectedTask._id].teamSize || 0} members voted
                                            {taskValidationVotes[selectedTask._id].minRequiredVotes && (
                                              <span className="block mt-1 text-orange-600 font-medium">
                                                Minimum requis: {taskValidationVotes[selectedTask._id].minRequiredVotes} votes
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Boutons de vote */}
                                      <div className="flex gap-3">
                                        <button
                                          onClick={() => handleVoteOnValidation(selectedTask._id, 'valid')}
                                          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                                            userValidationVote[selectedTask._id]?.vote === 'valid'
                                              ? 'bg-green-600 text-white'
                                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                                          }`}
                                        >
                                          <CheckCircle className="h-4 w-4 inline mr-1" /> Valide
                                        </button>
                                        <button
                                          onClick={() => handleVoteOnValidation(selectedTask._id, 'invalid')}
                                          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                                            userValidationVote[selectedTask._id]?.vote === 'invalid'
                                              ? 'bg-red-600 text-white'
                                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                                          }`}
                                        >
                                          <X className="h-4 w-4 inline mr-1" /> Non Valide
                                        </button>
                                      </div>
                                      {userValidationVote[selectedTask._id] && (
                                        <p className="text-xs text-purple-700 mt-2">
                                          You voted: <strong>{userValidationVote[selectedTask._id].vote === 'valid' ? 'Valid' : 'Invalid'}</strong>
                                          {userValidationVote[selectedTask._id].comment && (
                                            <span className="ml-2">({userValidationVote[selectedTask._id].comment})</span>
                                          )}
                                        </p>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                          </>
                        ) : (
                          <div className="space-y-4">
                          <div className="text-center py-8 text-gray-500">
                            <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                            <p className="text-lg font-medium mb-2">This task has not been submitted for validation yet</p>
                            {selectedTask.status === 'in-progress' ? (
                              <div className="space-y-4">
                                <p className="text-sm">You can submit this task for the project creator to validate.</p>
                                <button
                                  onClick={() => handleSubmitTask(selectedTask._id)}
                                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                >
                                  <Send className="h-4 w-4 inline mr-2" />
                                  Soumettre pour validation
                                </button>
                              </div>
                            ) : selectedTask.status === 'todo' ? (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4 max-w-md mx-auto">
                                <p className="text-sm text-yellow-900">
                                  <strong>⚠️ Note :</strong> The task must be "In progress" before it can be submitted.
                                </p>
                                <p className="text-xs text-yellow-800 mt-2">
                                  Change the task status to "In progress" in the "Details" tab.
                                </p>
                              </div>
                            ) : (
                              <p className="text-sm">
                                The task must be "In progress" to be submitted.
                              </p>
                            )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {taskDetailTab === 'feedback' && taskValidations[selectedTask._id]?.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold">Feedback de validation</h4>
                    {taskValidations[selectedTask._id].map((validation: any, idx: number) => (
                      <div key={idx} className={`border rounded-lg p-4 ${
                        validation.status === 'approved' ? 'bg-green-50 border-green-200' :
                        validation.status === 'revision-requested' ? 'bg-yellow-50 border-yellow-200' :
                        'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className={`inline-block px-3 py-1 rounded-full text-sm ${
                              validation.status === 'approved' ? 'bg-green-200 text-green-800' :
                              validation.status === 'revision-requested' ? 'bg-yellow-200 text-yellow-800' :
                              'bg-red-200 text-red-800'
                            }`}>
                              {validation.status === 'approved' ? 'Approved' :
                               validation.status === 'revision-requested' ? 'Revision requested' :
                               'Rejected'}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(validation.validatedAt).toLocaleDateString('en-US', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          {validation.validator && (
                            <p className="text-sm text-gray-600">
                              Par: {typeof validation.validator === 'object' ? validation.validator.name : validation.validator}
                            </p>
                          )}
                        </div>
                        {validation.feedback && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-1">Feedback:</p>
                            <p className="text-sm text-gray-900">{validation.feedback}</p>
                          </div>
                        )}
                        {validation.pointsToFix && validation.pointsToFix.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-1">Points to fix:</p>
                            <ul className="list-disc list-inside text-sm text-gray-900 space-y-1">
                              {validation.pointsToFix.map((point: string, pIdx: number) => (
                                <li key={pIdx}>{point}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

// TimelineView Component removed - now in TimelinePage.tsx

// Planning Poker Component for Project Level
const PlanningPokerContent: React.FC<{ projectId: string; project: any; user: any }> = ({ projectId, project, user }) => {
  const API_BASE = API_BASE_URL;
  const [tasks, setTasks] = useState<any[]>([]);
  const [currentTask, setCurrentTask] = useState<any | null>(null);
  const [currentVote, setCurrentVote] = useState<any | null>(null);
  const [selectedEstimation, setSelectedEstimation] = useState<number | null>(null);
  const [customHours, setCustomHours] = useState<string>('');
  const [showReveal, setShowReveal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskVotes, setTaskVotes] = useState<Record<string, any>>({});

  const planningPokerValues = [
    { label: '1', hours: 1 },
    { label: '2', hours: 2 },
    { label: '3', hours: 3 },
    { label: '5', hours: 5 },
    { label: '8', hours: 8 },
    { label: '13', hours: 13 },
    { label: '21', hours: 21 }
  ];

  // Get the first team of the project
  const teamId = project?.teams && project.teams.length > 0 ? project.teams[0]._id : null;

  useEffect(() => {
    if (teamId) {
      fetchTasks();
    }
  }, [teamId, project]);

  const fetchTasks = async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/tasks/team/${teamId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const fetchedTasks = Array.isArray(res.data.data?.tasks) ? res.data.data.tasks : [];
      setTasks(fetchedTasks);
      
      // Récupérer les votes pour toutes les tâches
      const votesMap: Record<string, any> = {};
      for (const task of fetchedTasks) {
        try {
          const voteRes = await axios.get(`${API_BASE}/votes/task/${task._id}/time-estimation`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          if (voteRes.data.success) {
            votesMap[task._id] = voteRes.data.data;
          }
        } catch (err: any) {
          // Ignorer les erreurs 404 (pas de vote)
        }
      }
      setTaskVotes(votesMap);
      
      const unestimatedTask = fetchedTasks.find((t: any) => !t.estimatedDuration || t.estimatedDuration === 0);
      if (unestimatedTask) {
        setCurrentTask(unestimatedTask);
        await fetchVoteForTask(unestimatedTask._id);
      } else if (fetchedTasks.length > 0) {
        setCurrentTask(fetchedTasks[0]);
        await fetchVoteForTask(fetchedTasks[0]._id);
      }
    } catch (err: any) {
      console.error('Erreur chargement tâches:', err);
      setError('Error loading tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchVoteForTask = async (taskId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/votes/task/${taskId}/time-estimation`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        const voteData = res.data.data;
        setCurrentVote(voteData);
        // Révéler si : vote fermé, ou realtime activé, ou tous les members voted
        const shouldReveal = voteData.status === 'closed' 
          || voteData.resultsVisibility === 'realtime' 
          || voteData.allMembersVoted === true;
        setShowReveal(shouldReveal);
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Erreur récupération vote:', err);
      }
      setCurrentVote(null);
    }
  };

  const selectTask = async (task: any) => {
    setCurrentTask(task);
    setSelectedEstimation(null);
    setCustomHours('');
    setShowReveal(false);
    await fetchVoteForTask(task._id);
  };

  const createVoteForTask = async (task: any) => {
    try {
      setSubmitting(true);
      setError(null);
      const res = await axios.post(
        `${API_BASE}/votes/task/${task._id}/time-estimation`,
        { endDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success) {
        await fetchVoteForTask(task._id);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error creating the vote');
    } finally {
      setSubmitting(false);
    }
  };

  const submitEstimation = async () => {
    if (!currentTask || !currentVote) return;
    
    if (selectedEstimation === null && !customHours) {
      setError('Please select an estimate');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const payload: any = {};
      if (customHours && customHours.trim()) {
        const hours = parseFloat(customHours);
        if (isNaN(hours) || hours < 0) {
          setError('Veuillez entrer un nombre d\'heures valide');
          setSubmitting(false);
          return;
        }
        payload.customHours = hours;
      } else if (selectedEstimation !== null) {
        payload.selectedOptions = [{ optionIndex: selectedEstimation }];
      }
      
      const res = await axios.post(
        `${API_BASE}/votes/${currentVote._id}/vote`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      await fetchVoteForTask(currentTask._id);
      // Si tous ont voté, révéler automatiquement
      if (res.data.allMembersVoted || res.data.resultsRevealed) {
        setShowReveal(true);
      }
      setSelectedEstimation(null);
      setCustomHours('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la soumission de l\'estimation');
    } finally {
      setSubmitting(false);
    }
  };

  const revealVotes = () => {
    setShowReveal(true);
  };

  const applyEstimation = async () => {
    if (!currentTask || !currentVote) return;
    
    try {
      setSubmitting(true);
      setError(null);
      await axios.post(
        `${API_BASE}/votes/${currentVote._id}/apply-estimation`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      await fetchTasks();
      await fetchVoteForTask(currentTask._id);
      // Rafraîchir les votes après application
      const votesMap: Record<string, any> = {};
      for (const task of tasks) {
        try {
          const voteRes = await axios.get(`${API_BASE}/votes/task/${task._id}/time-estimation`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          if (voteRes.data.success) {
            votesMap[task._id] = voteRes.data.data;
          }
        } catch (err: any) {
          // Ignorer les erreurs 404
        }
      }
      setTaskVotes(votesMap);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de l\'application de l\'estimation');
    } finally {
      setSubmitting(false);
    }
  };

  const completedTasks = tasks.filter((t: any) => t.estimatedDuration && t.estimatedDuration > 0).length;
  const totalTasks = tasks.length;
  const progressPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span className="font-medium">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Current Task Section */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Current task</h2>
              {currentTask ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                  <p className="text-lg font-semibold text-gray-900">{currentTask.title}</p>
                </div>
              ) : (
                <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 mb-3">
                  <p className="text-gray-500">No task selected</p>
                </div>
              )}
              <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-600">Select your estimate below</p>
              </div>
            </div>

            {/* Estimation Selection */}
            {currentTask && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Choisissez votre estimation</h2>
                
                {!currentVote ? (
                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <p className="text-gray-600 mb-4">No estimation votes for this task</p>
                    <button
                      onClick={() => createVoteForTask(currentTask)}
                      disabled={submitting}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                    >
                      {submitting ? 'Creating...' : 'Create a vote'}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-4 gap-3 mb-6">
                      {planningPokerValues.map((value, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedEstimation(index);
                            setCustomHours('');
                          }}
                          disabled={currentVote.userHasVoted || submitting}
                          className={`bg-white border-2 rounded-lg p-6 text-center transition-all ${
                            selectedEstimation === index && !customHours
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-gray-200 hover:border-gray-300'
                          } ${currentVote.userHasVoted ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span className="text-2xl font-bold text-gray-900">{value.label}</span>
                        </button>
                      ))}
                    </div>

                    {currentVote.userHasVoted ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="text-center mb-3">
                          <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-2" />
                          <p className="text-green-700 font-medium">You already voted</p>
                        </div>
                        {/* Afficher qui a voté et qui n'a pas voté */}
                        {currentVote.teamId && Array.isArray(currentVote.teamId.members) && currentVote.teamId.members.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-green-200">
                            <p className="text-xs font-semibold text-gray-700 mb-2">
                              Progression: {currentVote.votedMembers?.length || 0}/{currentVote.eligibleMembers?.length || currentVote.teamId.members.length} membres
                            </p>
                            <div className="space-y-1.5 max-h-32 overflow-y-auto">
                              {currentVote.teamId.members.map((member: any) => {
                                const memberId = member._id?.toString() || member.toString();
                                const hasVoted = currentVote.votedMembers?.includes(memberId);
                                return (
                                  <div key={memberId} className="flex items-center gap-2 text-xs">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                                      hasVoted 
                                        ? 'bg-green-100 text-green-700 border border-green-300' 
                                        : 'bg-gray-100 text-gray-500 border border-gray-300'
                                    }`}>
                                      {hasVoted ? '✓' : '○'}
                                    </div>
                                    <span className={`flex-1 ${hasVoted ? 'text-gray-700' : 'text-gray-500'}`}>
                                      {typeof member === 'object' ? (member.name || member.email || 'Membre') : 'Membre'}
                                    </span>
                                    {hasVoted && (
                                      <span className="text-green-600 text-[10px]">Voted</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {(currentVote.remainingVotes || 0) > 0 && (
                              <p className="text-xs text-orange-600 mt-2 font-medium">
                                ⏳ Waiting for {currentVote.remainingVotes} vote{currentVote.remainingVotes > 1 ? 's' : ''}
                              </p>
                            )}
                            {currentVote.allMembersVoted && (
                              <p className="text-xs text-green-600 font-semibold mt-2 text-center bg-green-100 rounded px-2 py-1">
                                ✓ All members voted — results revealed automatically
                              </p>
                            )}
                          </div>
                        )}
                        {!showReveal && currentVote.resultsVisibility === 'hidden' && !currentVote.allMembersVoted && (
                          <p className="text-xs text-gray-500 mt-2 text-center italic">
                            Results will be revealed automatically once all members have voted
                          </p>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={submitEstimation}
                        disabled={(selectedEstimation === null && !customHours) || submitting}
                        className="w-full bg-blue-600 text-white rounded-lg py-3 px-4 font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Envoi...
                          </>
                        ) : (
                          'Soumettre l\'estimation'
                        )}
                      </button>
                    )}

                    {/* Reveal Results - Seulement si tous ont voté ou si resultsVisibility est realtime */}
                    {showReveal && currentVote && (currentVote.allMembersVoted || currentVote.resultsVisibility === 'realtime' || currentVote.status === 'closed') && (currentVote.estimatedHours !== undefined && currentVote.estimatedHours !== null || currentVote.results?.averageHours !== undefined && currentVote.results?.averageHours !== null) && (
                      <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Results</h3>
                        {currentVote.allMembersVoted && (
                          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-sm text-green-700 font-medium text-center">
                              ✓ All members voted — results revealed
                            </p>
                          </div>
                        )}
                        <div className="text-center">
                          <p className="text-3xl font-bold text-blue-600 mb-2">
                            {currentVote.estimatedHours !== undefined && currentVote.estimatedHours !== null
                              ? currentVote.estimatedHours.toFixed(1)
                              : currentVote.results?.averageHours?.toFixed(1) || '0'} heures
                          </p>
                          <p className="text-gray-600">
                            ({Math.ceil((currentVote.estimatedHours || currentVote.results?.averageHours || 0) / 8)} jour(s))
                          </p>
                          <p className="text-sm text-gray-500 mt-2">
                            {currentVote.results?.totalVotes || currentVote.votedMembers?.length || 0} vote{(currentVote.results?.totalVotes || currentVote.votedMembers?.length || 0) > 1 ? 's' : ''}
                          </p>
                          {(() => {
                            const taskCreatorId = typeof currentTask.createdBy === 'object'
                              ? currentTask.createdBy?._id
                              : currentTask.createdBy;
                            return taskCreatorId === user?._id && (
                              <button
                                onClick={applyEstimation}
                                disabled={submitting}
                                className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300"
                              >
                                {submitting ? 'Application...' : 'Appliquer l\'estimation'}
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Task List */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Project tasks ({tasks.length})
              </h2>
              <div className="space-y-2">
                {tasks.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-500">
                    No tasks
                  </div>
                ) : (
                  tasks.map((task: any) => {
                    const isCompleted = task.estimatedDuration && task.estimatedDuration > 0;
                    const isActive = currentTask?._id === task._id;
                    const vote = taskVotes[task._id];
                    const hasVote = vote && (vote.estimatedHours !== undefined || vote.results?.averageHours !== undefined);
                    const estimatedHours = vote?.estimatedHours || vote?.results?.averageHours || 0;
                    
                    return (
                      <div
                        key={task._id}
                        onClick={() => selectTask(task)}
                        className={`bg-white border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          isActive
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {isCompleted ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                          ) : isActive ? (
                            <Play className="h-5 w-5 text-blue-600 fill-blue-600 flex-shrink-0" />
                          ) : (
                            <div className="h-5 w-5 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <span className={`block ${isActive ? 'font-semibold' : ''}`}>
                              {task.title}
                            </span>
                            {hasVote && !isCompleted && (
                              <span className="text-xs text-purple-600 mt-1 block">
                                Estimation: {estimatedHours.toFixed(1)}h ({vote.results?.totalVotes || 0} vote{vote.results?.totalVotes > 1 ? 's' : ''})
                              </span>
                            )}
                            {isCompleted && task.estimatedDuration && (
                              <span className="text-xs text-green-600 mt-1 block">
                                {task.estimatedDuration} jour{task.estimatedDuration > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            {/* Progress */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Progression</h2>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  {completedTasks}/{totalTasks}
                </p>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectInterfacePage;
